import { useEffect, useRef, useState, useCallback } from "react";
import OrbitRankAd from "@/components/OrbitRankAd";
import ChatWidget from "@/components/ChatWidget";
import ThemeToggle from "@/components/ThemeToggle";
import MobileMenu from "@/components/landing/MobileMenu";
import TweetTestimonials from "@/components/landing/TweetTestimonials";
import GrowthEngineSection from "@/components/landing/GrowthEngineSection";
import LiveDemoOverlay from "@/components/landing/LiveDemoOverlay";
import DesktopHero from "@/components/landing/DesktopHero";
import TrustBridge from "@/components/landing/TrustBridge";

import { Menu, FileText, BarChart3, Link2, Target, Zap, FileBarChart, ArrowRight, ShieldCheck, Settings, Search, Code2, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useNavigate } from "react-router-dom";
import { usePaddleCheckout } from "@/hooks/usePaddleCheckout";
import { supabase } from "@/integrations/supabase/client";
import "@/styles/landing.css";

import logoMark from "@/assets/orbitrank-mark.png";
import brainGreenImg from "@/assets/ai-brain-orbit-green.png";
import logoMarkTransparent from "@/assets/orbitrank-mark-transparent-clean.png";
import logoFull from "@/assets/orbitrank-logo-v3.png";
import wordpressLogo from "@/assets/integrations/wordpress.svg";
import shopifyLogo from "@/assets/integrations/shopify.svg";
import squarespaceLogo from "@/assets/integrations/squarespace.svg";
import webflowLogo from "@/assets/integrations/webflow.svg";
import googleLogo from "@/assets/integrations/google.svg";
import chatgptLogo from "@/assets/integrations/chatgpt.svg";
import geminiLogo from "@/assets/integrations/gemini.svg";
import claudeLogo from "@/assets/integrations/claude.svg";

// ─── Counter Hook ─────────────────────────
const useCountUp = (target: number, duration = 2500) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const s = performance.now();
        const tick = (now: number) => {
          const p = Math.min((now - s) / duration, 1);
          setValue(Math.round((1 - Math.pow(1 - p, 3)) * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return { value, ref };
};

const useLiveCounter = (base: number, maxBump: number) => {
  const [value, setValue] = useState(base);
  const ref = useRef<HTMLDivElement>(null);
  const visible = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { visible.current = e.isIntersecting; }, { threshold: 0.3 });
    obs.observe(el);
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (visible.current) {
        setValue(v => v + Math.floor(Math.random() * maxBump) + 1);
      }
      timer = setTimeout(tick, 8000 + Math.random() * 4000);
    };
    timer = setTimeout(tick, 8000);
    return () => { obs.disconnect(); clearTimeout(timer); };
  }, [base, maxBump]);
  return { value, ref };
};

// ─── Avatars ──────────────────────────────
const AVATARS = [
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
];

// ─── Platform logos ───────────────────────
const PLATFORMS = [
  { name: "WordPress", logo: wordpressLogo },
  { name: "Shopify", logo: shopifyLogo },
  { name: "Squarespace", logo: squarespaceLogo },
  { name: "Webflow", logo: webflowLogo },
  { name: "Google", logo: googleLogo },
];

// ─── Nav ──────────────────────────────────
const Nav = () => {
  const { theme } = useTheme();
  const isLightMode = theme === "light";
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        display: "flex", justifyContent: "space-between", alignItems: "center",
        height: 56,
        padding: "12px 20px",
        overflow: "visible",
        background: "var(--lo-nav-bg)", backdropFilter: "blur(20px)",
        borderBottom: "1px solid var(--lo-border)",
      }}>
        <img
          src={logoFull}
          alt="OrbitRank logo"
          style={{ height: "28px", width: "auto", maxWidth: "none" }}
        />

        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu"
          aria-expanded={menuOpen}
          style={{
            width: 40, height: 40, borderRadius: 8,
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            background: "transparent", border: "1px solid var(--lo-border)",
            cursor: "pointer", color: "var(--lo-text)",
          }}
        >
          <Menu size={20} />
        </button>
      </nav>
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  );
};

// ─── Demo Banner ──────────────────────────
const DemoBanner = () => {
  return (
    <a
      href="https://orbitrank.dev/demo"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "block",
        width: "100%",
        background: "#00FF88",
        color: "#000000",
        textAlign: "center",
        padding: "12px 16px",
        fontSize: "14px",
        fontWeight: 800,
        letterSpacing: "0.02em",
        textDecoration: "none",
        animation: "demoBannerGlow 2s ease-in-out infinite",
        position: "relative",
        zIndex: 90,
        marginTop: 56,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#00F090";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#00FF88";
      }}
    >
      <span style={{ marginRight: 8 }}>🚀</span>
      Try Live Demo &rarr;
    </a>
  );
};

// ─── Hero ──────────────────────────────────
const Hero = () => {
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const openDemo = () => {
    import("@/lib/analytics").then(({ trackEvent }) => trackEvent("cta_live_demo_overlay"));
    setDemoOpen(true);
  };
  return (
    <section className="lo-hero" style={{ position: "relative" }}>
      <LiveDemoOverlay open={demoOpen} onClose={() => setDemoOpen(false)} />
      <div className="lo-hero-trial-wrap">
        <button
          type="button"
          onClick={() => {
            import("@/lib/analytics").then(({ trackEvent }) => trackEvent("cta_free_trial"));
            navigate("/activate");
          }}
          className="lo-hero-trial-link"
        >
          <span className="lo-hero-trial-main">✨ Start 7-day free trial &rarr;</span>
        </button>
      </div>
      <div className="lo-hero-badge"><span className="lo-pulse" /> engine running · publishing now</div>
      <h1>Rank everywhere.<br /><span>Automatically.</span></h1>
      <p className="lo-hero-sub">OrbitRank deploys fresh SEO pages to your website every single day. No agency. No effort. Just growth.</p>

      {/* Buttons first on mobile */}
      <div className="lo-hero-btns">
        <button
          type="button"
          className="lo-btn-primary lo-btn-demo-hero"
          onClick={() => {
            import("@/lib/analytics").then(({ trackEvent }) => trackEvent("cta_start_growing"));
            navigate("/activate");
          }}
        >
          ✨ Start Growing &rarr;
        </button>
        <button
          type="button"
          className="lo-btn-secondary"
          onClick={() => {
            import("@/lib/analytics").then(({ trackEvent }) => trackEvent("cta_start_growing"));
            navigate("/activate");
          }}
        >
          Start Free Trial
        </button>
        <button
          type="button"
          className="lo-hero-demo-link"
          onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
        >
          See How It Works &rarr;
        </button>
      </div>

      {/* OrbitRank Ad Animation */}
      <div style={{width:"100%",display:"flex",justifyContent:"center",padding:"40px 16px"}}>
        <iframe
          src="https://resonant-peony-6b055b.netlify.app"
          width="380"
          height="380"
          frameBorder="0"
          scrolling="no"
          style={{border:"none",borderRadius:16}}
        />
      </div>
    </section>
  );
};

// ─── Found Everywhere ─────────────────────
const FoundEverywhere = () => {
  const engines = [
    { name: "Google", logo: googleLogo },
    { name: "ChatGPT", logo: chatgptLogo },
    { name: "Gemini", logo: geminiLogo },
    { name: "Claude", logo: claudeLogo },
  ];
  // Duplicate enough times for seamless loop
  const loop = [...engines, ...engines, ...engines, ...engines];
  return (
    <section style={{
      background: "#000000",
      padding: "56px 0",
      borderTop: "1px solid rgba(255,255,255,0.06)",
      textAlign: "center",
    }}>
      <h2 style={{
        fontSize: "clamp(22px, 5.5vw, 36px)",
        fontWeight: 900,
        letterSpacing: "-0.5px",
        lineHeight: 1.15,
        color: "var(--lo-text)",
        margin: "0 auto 32px",
        maxWidth: 680,
        padding: "0 24px",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      }}>
        Get found on Google, ChatGPT, Gemini &amp; Claude.
      </h2>
      <div style={{
        position: "relative",
        overflow: "hidden",
        maskImage: "linear-gradient(to right, transparent 0%, #000 12%, #000 88%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 12%, #000 88%, transparent 100%)",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 56,
          width: "max-content",
          animation: "foundMarquee 32s linear infinite",
        }}>
          {loop.map((e, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <img
                src={e.logo}
                alt={`${e.name} logo`}
                style={{ width: 48, height: 48, objectFit: "contain", opacity: 0.85 }}
                loading="lazy"
              />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--lo-text-soft)", letterSpacing: 0.3 }}>
                {e.name}
              </span>
            </div>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes foundMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};




// ─── Social Proof ─────────────────────────
const SocialProof = () => {
  const pages = useCountUp(750000);
  const businesses = useCountUp(5000);
  return (
    <section className="lo-social-proof">
      <div className="lo-proof-row">
        <div className="lo-proof-avatars">
          {AVATARS.map((src, i) => (
            <div key={i} className="lo-avatar"><img src={src} alt="Customer" loading="lazy" /></div>
          ))}
        </div>
        <div className="lo-proof-score">
          <div className="lo-score-num">4.9 ⭐⭐⭐⭐⭐</div>
          <div style={{ fontSize: 11, color: "var(--lo-text-muted)" }}>from 5,000+ businesses</div>
        </div>
      </div>
      <div className="lo-proof-counts">
        <div className="lo-proof-count">
          <div ref={pages.ref} className="lo-proof-count-val">{pages.value.toLocaleString()}</div>
          <div className="lo-proof-count-lbl">Pages Generated</div>
        </div>
        <div className="lo-proof-count">
          <div ref={businesses.ref} className="lo-proof-count-val">{businesses.value.toLocaleString()}</div>
          <div className="lo-proof-count-lbl">Businesses</div>
        </div>
      </div>
    </section>
  );
};

// ─── Marquee ──────────────────────────────
const Marquee = () => {
  const items = [...PLATFORMS, ...PLATFORMS];
  return (
    <section className="lo-marquee-section">
      <div className="lo-marquee-label">Works with your website</div>
      <div className="lo-marquee-track">
        {items.map((p, i) => (
          <div key={i} className="lo-plat">
            <div className="lo-plat-icon">
              <img src={p.logo} alt={p.name} />
            </div>
            <span className="lo-plat-name">{p.name}</span>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── AI Search Intelligence Section ───────
const AiVisibilitySection = () => {
  const PURPLE = "#A78BFA";
  const PURPLE_DEEP = "#8B5CF6";
  const BLUE = "#60A5FA";
  const GREEN = "#34D399";

  const features = [
    {
      title: "While you focus on rankings,",
      sub: "AI is quietly recommending your competitors.",
      color: PURPLE,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.8H20l-5 3.6 1.9 5.8L12 14.6 7.1 18.2 9 12.4 4 8.8h6.1z"/></svg>
      ),
    },
    {
      title: "See if your business appears in AI answers —",
      sub: "or if you're completely invisible.",
      color: BLUE,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="14" rx="3"/><path d="M8 6V4M16 6V4M9 13h.01M15 13h.01M9 17h6"/></svg>
      ),
    },
    {
      title: "Understand why AI recommends",
      sub: "competitors instead of you.",
      color: GREEN,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 20h18M6 20V10M11 20V4M16 20v-7M21 20v-3"/></svg>
      ),
    },
    {
      title: "Get cited across the web so AI engines trust",
      sub: "and mention your business.",
      color: PURPLE_DEEP,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18"/></svg>
      ),
    },
  ];

  const engines = [
    { name: "ChatGPT", color: GREEN, dot: "#10A37F" },
    { name: "Claude", color: "#D97757", dot: "#D97757" },
    { name: "Gemini", color: BLUE, dot: "#4285F4" },
    { name: "Google AI", color: "#FBBC05", dot: "#FBBC05" },
    { name: "Perplexity", color: "#22D3EE", dot: "#22D3EE" },
    { name: "Bing Copilot", color: "#A78BFA", dot: "#A78BFA" },
    { name: "Grok", color: "#94A3B8", dot: "#CBD5E1" },
    { name: "Meta AI", color: "#6366F1", dot: "#818CF8" },
  ];

  const results = [
    { name: "Competitor.com", tag: "Recommended", highlight: true, color: PURPLE },
    { name: "AnotherTool.io", tag: "Not mentioned", highlight: false },
    { name: "YourBusiness.com", tag: "Not mentioned", highlight: false, you: true },
  ];

  const metrics = [
    { label: "Mentions", value: "132", delta: "+23%", color: PURPLE, path: "M0 18 L15 14 L30 16 L45 9 L60 12 L75 6 L90 10" },
    { label: "Citations", value: "43", delta: "+18%", color: BLUE, path: "M0 16 L15 12 L30 15 L45 8 L60 11 L75 5 L90 8" },
    { label: "AI Visibility Score", value: "87", delta: "+14%", color: "#F472B6", path: "M0 17 L15 13 L30 15 L45 9 L60 11 L75 7 L90 9" },
  ];

  return (
    <section
      style={{
        position: "relative",
        padding: "96px 24px",
        background: "#000000",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
        overflow: "hidden",
      }}
    >
      {/* background glow removed — clean black section */}
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          position: "relative",
          zIndex: 1,
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.15fr)",
          gap: 48,
          alignItems: "stretch",
        }}
        className="ai-search-grid"
      >
        {/* LEFT */}
        <div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "7px 14px",
              borderRadius: 999,
              background: "rgba(139,92,246,0.10)",
              border: "1px solid rgba(139,92,246,0.35)",
              color: "#C4B5FD",
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              marginBottom: 24,
            }}
          >
            <span style={{ color: "#FBBF24" }}>✦</span> AI Search Intelligence
          </div>
          <h2
            style={{
              fontSize: "clamp(30px, 4.4vw, 52px)",
              fontWeight: 800,
              color: "#FFFFFF",
              lineHeight: 1.08,
              letterSpacing: "-0.025em",
              margin: "0 0 18px",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
            }}
          >
            AI is already choosing<br />who gets your customers.<br />
            <span
              style={{
                background: `linear-gradient(90deg, ${PURPLE} 0%, ${BLUE} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              You're just not in<br />the conversation. Yet.
            </span>
          </h2>
          <div
            style={{
              borderLeft: `2px solid ${PURPLE_DEEP}`,
              padding: "4px 0 4px 16px",
              margin: "24px 0 32px",
            }}
          >
            <p style={{ fontSize: 15.5, lineHeight: 1.65, color: "#9CA3AF", margin: 0 }}>
              People aren't just searching on Google anymore.<br />
              They're asking ChatGPT and Gemini who to trust —<br />
              and those answers are already deciding who gets the call.
            </p>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            {features.map((f, i) => (
              <div
                key={i}
                className="ai-feat-card"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  padding: "16px 18px",
                  background: "linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 14,
                  cursor: "pointer",
                  transition: "border-color .25s ease, transform .25s ease, background .25s ease",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 10,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: f.color,
                    background: `radial-gradient(circle, ${f.color}22 0%, ${f.color}08 70%, transparent 100%)`,
                    border: `1px solid ${f.color}40`,
                  }}
                >
                  {f.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 14.5, lineHeight: 1.35 }}>
                    {f.title}
                  </div>
                  <div style={{ color: "#94A3B8", fontSize: 13.5, lineHeight: 1.45, marginTop: 2 }}>
                    {f.sub}
                  </div>
                </div>
                <span style={{ color: "#475569", fontSize: 18, flexShrink: 0 }}>›</span>
              </div>
            ))}
          </div>

          <div
            className="hidden md:flex ai-section-cta-row"
            style={{
              marginTop: 28,
              justifyContent: "center",
              alignItems: "center",
              gap: 14,
              flexWrap: "nowrap",
            }}
          >
            <a href="/activate" className="lo-btn-primary" style={{ display: "inline-block", width: "auto", textDecoration: "none" }}>
              🚀 Launch My SEO →
            </a>
            <a
              href="/blog"
              className="ai-engines-learn-cta"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: "15px 24px",
                borderRadius: 11,
                background: "transparent",
                border: `1px solid ${PURPLE}66`,
                color: PURPLE,
                fontWeight: 800,
                fontSize: 14,
                cursor: "pointer",
                boxShadow: `0 0 0 1px ${PURPLE}22 inset, 0 6px 18px ${PURPLE}1f`,
                textDecoration: "none",
                transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease, color .2s ease",
                maxWidth: "100%",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              Learn how AI engines decide which businesses to cite →
            </a>
          </div>
          <style>{`
            .ai-engines-learn-cta:hover {
              transform: translateY(-1px);
              border-color: ${PURPLE} !important;
              color: #C4B5FD !important;
              box-shadow: 0 0 0 1px ${PURPLE}44 inset, 0 10px 26px ${PURPLE}33 !important;
            }
          `}</style>
        </div>

        {/* RIGHT — AI Search dashboard */}
        <div
          style={{
            position: "relative",
            borderRadius: 22,
            border: "1px solid rgba(255,255,255,0.08)",
            background:
              "radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.10) 0%, transparent 60%), linear-gradient(180deg, #0A0712 0%, #050308 100%)",
            padding: "22px 22px 26px",
            overflow: "hidden",
            minHeight: 520,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
          className="ai-search-panel"
        >
          {/* Status */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: GREEN, boxShadow: `0 0 10px ${GREEN}` }} />
            <span style={{ color: "#CBD5E1", fontSize: 12.5, fontWeight: 600 }}>
              OrbitRank AI Search Intelligence in action
            </span>
          </div>

          {/* Search bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 16px",
              borderRadius: 999,
              border: `1px solid ${PURPLE_DEEP}55`,
              background: "rgba(139,92,246,0.06)",
              marginBottom: 22,
              boxShadow: `0 0 24px ${PURPLE_DEEP}22 inset`,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={PURPLE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
            </svg>
            <span style={{ color: "#CBD5E1", fontSize: 13.5 }}>Best marketing automation tool for agencies?</span>
          </div>

          {/* Main grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 0.8fr) minmax(0, 1.4fr) minmax(0, 0.85fr)",
              gap: 14,
              alignItems: "start",
            }}
            className="ai-search-inner"
          >
            {/* Engines */}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {engines.map((e) => (
                <div
                  key={e.name}
                  style={{
                    padding: "8px 10px",
                    borderRadius: 10,
                    border: `1px solid ${e.color}33`,
                    background: `linear-gradient(180deg, ${e.color}10, transparent)`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: `${e.dot}22`,
                      border: `1px solid ${e.dot}66`,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: e.dot,
                      fontSize: 9.5,
                      fontWeight: 800,
                      flexShrink: 0,
                    }}
                  >
                    {e.name[0]}
                  </span>
                  <span style={{ color: "#FFFFFF", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}</span>
                </div>
              ))}
            </div>

            {/* AI Overview */}
            <div
              style={{
                padding: "16px 16px",
                borderRadius: 14,
                border: `1px solid ${PURPLE_DEEP}40`,
                background: "linear-gradient(180deg, rgba(139,92,246,0.08), rgba(139,92,246,0.02))",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{ color: PURPLE, fontSize: 13 }}>✦</span>
                <span style={{ color: "#FFFFFF", fontWeight: 700, fontSize: 13.5 }}>AI Overview</span>
              </div>
              <p style={{ color: "#94A3B8", fontSize: 12, lineHeight: 1.5, margin: "0 0 14px" }}>
                Based on top sources, here are the best marketing automation tools for agencies.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {results.map((r) => (
                  <div
                    key={r.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      borderRadius: 10,
                      border: r.highlight ? `1px solid ${PURPLE_DEEP}` : "1px solid rgba(255,255,255,0.06)",
                      background: r.highlight ? "rgba(139,92,246,0.10)" : "rgba(255,255,255,0.02)",
                      boxShadow: r.highlight ? `0 0 18px ${PURPLE_DEEP}33` : "none",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                      <span
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 999,
                          background: r.highlight ? PURPLE_DEEP : "rgba(255,255,255,0.06)",
                          color: "#FFFFFF",
                          fontSize: 10,
                          fontWeight: 800,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {r.you ? "Y" : r.name[0]}
                      </span>
                      <span style={{ color: "#E5E7EB", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {r.name}
                      </span>
                    </div>
                    <span
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: r.highlight ? PURPLE : "#64748B",
                        flexShrink: 0,
                      }}
                    >
                      {r.tag}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {metrics.map((m, mi) => (
                <div
                  key={m.label}
                  style={{
                    padding: "12px 12px",
                    borderRadius: 12,
                    border: "1px solid rgba(255,255,255,0.07)",
                    background: "rgba(255,255,255,0.02)",
                  }}
                >
                  <div style={{ color: "#94A3B8", fontSize: 10.5, marginBottom: 4, fontWeight: 600 }}>{m.label}</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span style={{ color: "#FFFFFF", fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em", animation: `metricTick 3.6s ease-in-out ${mi * 0.4}s infinite` }}>{m.value}</span>
                    <span style={{ color: GREEN, fontSize: 11, fontWeight: 700 }}>{m.delta}</span>
                  </div>
                  <svg width="100%" height="26" viewBox="0 0 90 22" preserveAspectRatio="none" style={{ marginTop: 4, display: "block", overflow: "visible" }}>
                    <path d={m.path} stroke={`${m.color}33`} strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    <path
                      d={m.path}
                      stroke={m.color}
                      strokeWidth="1.6"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      pathLength={100}
                      strokeDasharray="22 100"
                      style={{
                        animation: `sparkDraw 3.6s linear ${mi * 0.5}s infinite`,
                        filter: `drop-shadow(0 0 4px ${m.color})`,
                      }}
                    />
                  </svg>
                </div>
              ))}
            </div>
          </div>

          {/* AI Visibility Flow animation */}
          <div
            style={{
              marginTop: 22,
              padding: "18px 18px 16px",
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,0.07)",
              background:
                "radial-gradient(ellipse at 50% 0%, rgba(96,165,250,0.08) 0%, transparent 70%), linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ color: "#CBD5E1", fontSize: 11.5, fontWeight: 600, letterSpacing: "0.04em" }}>
                AI Visibility Pipeline
              </span>
              <span style={{ color: "#64748B", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Live
              </span>
            </div>

            <div style={{ position: "relative", height: 78 }}>
              {/* Connection line */}
              <svg
                width="100%"
                height="78"
                viewBox="0 0 400 78"
                preserveAspectRatio="none"
                style={{ position: "absolute", inset: 0, overflow: "visible" }}
              >
                <defs>
                  <linearGradient id="flowLine" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={PURPLE_DEEP} stopOpacity="0.15" />
                    <stop offset="50%" stopColor={BLUE} stopOpacity="0.55" />
                    <stop offset="100%" stopColor={GREEN} stopOpacity="0.15" />
                  </linearGradient>
                </defs>
                <path d="M 32 39 L 368 39" stroke="url(#flowLine)" strokeWidth="1.2" fill="none" strokeDasharray="3 4" opacity="0.7" />
                <circle r="2.5" fill={BLUE} opacity="0.9">
                  <animateMotion dur="3.6s" repeatCount="indefinite" path="M 32 39 L 368 39" />
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="3.6s" repeatCount="indefinite" />
                </circle>
                <circle r="1.8" fill={PURPLE} opacity="0.7">
                  <animateMotion dur="3.6s" begin="1.2s" repeatCount="indefinite" path="M 32 39 L 368 39" />
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="3.6s" begin="1.2s" repeatCount="indefinite" />
                </circle>
                <circle r="1.5" fill={GREEN} opacity="0.6">
                  <animateMotion dur="3.6s" begin="2.4s" repeatCount="indefinite" path="M 32 39 L 368 39" />
                  <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.1;0.9;1" dur="3.6s" begin="2.4s" repeatCount="indefinite" />
                </circle>
              </svg>

              {/* Nodes */}
              <div style={{ position: "absolute", inset: 0, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 4px" }}>
                {[
                  { label: "Query", color: PURPLE, icon: "🔍", delay: "0s" },
                  { label: "AI Analysis", color: BLUE, icon: "✦", delay: "0.9s" },
                  { label: "Citations", color: "#22D3EE", icon: "❝", delay: "1.8s" },
                  { label: "Visibility", color: GREEN, icon: "▲", delay: "2.7s" },
                ].map((n) => (
                  <div key={n.label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, position: "relative", zIndex: 1 }}>
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 999,
                        background: `radial-gradient(circle, ${n.color}30 0%, ${n.color}08 60%, transparent 100%)`,
                        border: `1px solid ${n.color}55`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: n.color,
                        fontSize: 13,
                        fontWeight: 700,
                        animation: `flowPulse 3.6s ease-in-out ${n.delay} infinite`,
                      }}
                    >
                      {n.icon}
                    </div>
                    <span style={{ color: "#94A3B8", fontSize: 10, fontWeight: 600, letterSpacing: "0.02em" }}>{n.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Live AI Signals card */}
          <div
            style={{
              marginTop: "auto",
              padding: "16px 18px",
              borderRadius: 14,
              border: "1px solid rgba(139,92,246,0.18)",
              background:
                "radial-gradient(ellipse at 80% 0%, rgba(139,92,246,0.06) 0%, transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: GREEN,
                    boxShadow: `0 0 8px ${GREEN}`,
                    animation: "livePulse 2.2s ease-in-out infinite",
                  }}
                />
                <span style={{ color: "#E2E8F0", fontSize: 12.5, fontWeight: 700, letterSpacing: "0.02em" }}>
                  Live AI Signals
                </span>
              </div>
              <span style={{ color: "#64748B", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                Real-time
              </span>
            </div>

            {/* Signal rows */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px" }}>
              {[
                { label: "AI Mentions", value: "132", delta: "+12", spark: "M0 10 L8 7 L16 9 L24 5 L32 8 L40 4 L48 6", color: PURPLE },
                { label: "Citation Growth", value: "43", delta: "+8", spark: "M0 8 L8 6 L16 8 L24 4 L32 7 L40 3 L48 5", color: BLUE },
                { label: "Visibility Score", value: "87", delta: "+5", spark: "M0 9 L8 6 L16 8 L24 5 L32 7 L40 4 L48 6", color: "#F472B6" },
                { label: "Trending Queries", value: "28", delta: "+3", spark: "M0 7 L8 5 L16 7 L24 4 L32 6 L40 3 L48 5", color: GREEN },
              ].map((s) => (
                <div
                  key={s.label}
                  style={{
                    padding: "10px 10px",
                    borderRadius: 10,
                    border: "1px solid rgba(255,255,255,0.05)",
                    background: "rgba(255,255,255,0.015)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ color: "#94A3B8", fontSize: 10, fontWeight: 600, letterSpacing: "0.02em" }}>{s.label}</span>
                    <span style={{ color: GREEN, fontSize: 9.5, fontWeight: 700 }}>+{s.delta}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                    <span style={{ color: "#FFFFFF", fontSize: 17, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.value}</span>
                  </div>
                  <svg width="100%" height="18" viewBox="0 0 48 14" preserveAspectRatio="none" style={{ display: "block", opacity: 0.7 }}>
                    <path d={s.spark} stroke={s.color} strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              ))}
            </div>

            {/* Bottom micro bar */}
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ flex: 1, height: 3, borderRadius: 999, background: "rgba(255,255,255,0.04)", overflow: "hidden" }}>
                <div
                  style={{
                    height: "100%",
                    width: "72%",
                    borderRadius: 999,
                    background: `linear-gradient(90deg, ${PURPLE_DEEP} 0%, ${BLUE} 100%)`,
                    animation: "signalBar 4s ease-in-out infinite alternate",
                  }}
                />
              </div>
              <span style={{ color: "#64748B", fontSize: 9.5, fontWeight: 600, whiteSpace: "nowrap" }}>Recommendation strength</span>
            </div>
          </div>

          {/* ambient purple blur removed — keeps card clean & contained */}
        </div>
      </div>

      <style>{`
        @keyframes flowPulse {
          0%, 100% { box-shadow: 0 0 0 0 transparent; transform: scale(1); }
          50% { box-shadow: 0 0 18px currentColor; transform: scale(1.08); }
        }
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.85); }
        }
        @keyframes signalBar {
          0% { width: 62%; }
          100% { width: 88%; }
        }
        @keyframes sparkDraw {
          0%   { stroke-dashoffset: 100; opacity: 0.2; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { stroke-dashoffset: -22; opacity: 0.2; }
        }
        @keyframes metricTick {
          0%, 60%, 100% { transform: translateY(0); opacity: 1; }
          70% { transform: translateY(-1px); opacity: 0.85; }
          80% { transform: translateY(0); opacity: 1; }
        }
        .ai-feat-card:hover {
          border-color: rgba(167,139,250,0.35) !important;
          background: linear-gradient(180deg, rgba(167,139,250,0.06) 0%, rgba(255,255,255,0.01) 100%) !important;
          transform: translateX(2px);
        }
        @media (max-width: 1024px) {
          .ai-search-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 720px) {
          .ai-search-inner {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

// ─── Flow Section ─────────────────────────
const FlowSection = () => <FlowSectionV2 />;

const FLOW_GREEN = "#00FF88";
const FLOW_PURPLE = "#A78BFA";
const FLOW_BLUE = "#60A5FA";
const FLOW_AMBER = "#FBBF24";

type FlowStage = {
  num: string;
  color: string;
  icon: React.ReactNode;
  title: string;
  desc: string;
  visual: React.ReactNode;
};

const Sparkline = ({ color, seed = 0 }: { color: string; seed?: number }) => {
  const pts = Array.from({ length: 24 }, (_, i) => {
    const x = (i / 23) * 100;
    const y = 30 - (Math.sin(i * 0.6 + seed) * 6 + (i / 23) * 14 + Math.cos(i * 0.9 + seed) * 3 + 8);
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 100 32" preserveAspectRatio="none" style={{ width: "100%", height: 44 }}>
      <defs>
        <linearGradient id={`sg-${seed}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 4px ${color})` }} />
      <polygon points={`0,32 ${pts} 100,32`} fill={`url(#sg-${seed})`} />
    </svg>
  );
};

const StageCard = ({ s }: { s: FlowStage }) => (
  <div
    className="flow-stage-card"
    style={{
      background: "linear-gradient(160deg, rgba(20,24,38,0.72), rgba(8,10,18,0.85))",
      border: `1px solid ${s.color}33`,
      borderRadius: 16,
      padding: "22px 22px",
      display: "flex",
      gap: 16,
      alignItems: "flex-start",
      boxShadow: `0 0 0 1px ${s.color}10 inset, 0 18px 50px -28px ${s.color}55`,
      transition: "transform .35s ease, box-shadow .35s ease, border-color .35s ease",
    }}
  >
    <div
      style={{
        width: 52,
        height: 52,
        borderRadius: 12,
        background: `radial-gradient(circle at 30% 30%, ${s.color}33, ${s.color}08 70%, transparent)`,
        border: `1px solid ${s.color}55`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: s.color,
        flexShrink: 0,
        boxShadow: `0 0 18px ${s.color}30`,
      }}
    >
      {s.icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: "#FFFFFF", letterSpacing: "-0.01em", marginBottom: 6 }}>
        {s.title}
      </div>
      <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "#9CA3AF" }}>{s.desc}</div>
    </div>
  </div>
);

const VisualPanel = ({ s, children }: { s: FlowStage; children: React.ReactNode }) => (
  <div
    style={{
      background: "linear-gradient(160deg, rgba(15,18,30,0.72), rgba(6,8,16,0.88))",
      border: `1px solid ${s.color}2a`,
      borderRadius: 16,
      padding: "20px 22px",
      minHeight: 180,
      display: "flex",
      gap: 22,
      alignItems: "stretch",
      boxShadow: `0 0 0 1px ${s.color}10 inset, 0 24px 60px -34px ${s.color}40`,
    }}
  >
    {children}
  </div>
);

const Chip = ({ label, color }: { label: string; color: string }) => (
  <span
    style={{
      display: "inline-block",
      padding: "6px 12px",
      borderRadius: 999,
      fontSize: 12,
      color: "#E5E7EB",
      background: `${color}12`,
      border: `1px solid ${color}3a`,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </span>
);

const FlowSectionV2 = () => {
  const stages: FlowStage[] = [
    {
      num: "01",
      color: FLOW_GREEN,
      icon: <span style={{ fontSize: 22 }}>🔍</span>,
      title: "Keywords Activated",
      desc: "High-intent local keywords detected and locked automatically for your market.",
      visual: (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 6 }}>Live Keywords Detected</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 32, fontWeight: 800, color: "#fff", letterSpacing: "-.02em" }}>1,284</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: FLOW_GREEN }}>+142</span>
            </div>
            <Sparkline color={FLOW_GREEN} seed={1} />
          </div>
          <div style={{ flex: 1.1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 10 }}>Top Keyword Examples</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              <Chip label="barbershop near me" color={FLOW_GREEN} />
              <Chip label="best coffee shop" color={FLOW_GREEN} />
              <Chip label="car detailing service" color={FLOW_GREEN} />
              <Chip label="digital marketing agency" color={FLOW_GREEN} />
              <Chip label="local gym" color={FLOW_GREEN} />
              <span style={{ fontSize: 12, color: FLOW_GREEN, alignSelf: "center", fontWeight: 700 }}>+842 more</span>
            </div>
          </div>
        </>
      ),
    },
    {
      num: "02",
      color: FLOW_PURPLE,
      icon: <span style={{ fontSize: 22 }}>⚙️</span>,
      title: "OrbitRank Engine",
      desc: "AI builds fully optimized SEO pages — schema, FAQs, images, internal links.",
      visual: (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 6 }}>Pages Built</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 32, fontWeight: 800, color: "#fff", letterSpacing: "-.02em" }}>324</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: FLOW_PURPLE }}>+38</span>
            </div>
            <Sparkline color={FLOW_PURPLE} seed={2} />
          </div>
          <div style={{ flex: 1.1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 10 }}>What's Included</div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6, fontSize: 13, color: "#E5E7EB" }}>
              {["SEO-optimized content", "Schema & structured data", "Internal linking & FAQs", "Image optimization"].map((t) => (
                <li key={t} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: FLOW_PURPLE, fontWeight: 800 }}>✓</span> {t}
                </li>
              ))}
            </ul>
          </div>
        </>
      ),
    },
    {
      num: "03",
      color: FLOW_BLUE,
      icon: <span style={{ fontSize: 22 }}>🚀</span>,
      title: "Pages Deployed Live",
      desc: "New pages go live on your site every morning. Automatically submitted to Google.",
      visual: (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 6 }}>Pages Live</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 32, fontWeight: 800, color: "#fff", letterSpacing: "-.02em" }}>324</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: FLOW_BLUE }}>+38</span>
            </div>
            <Sparkline color={FLOW_BLUE} seed={3} />
          </div>
          <div style={{ flex: 1.1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              Status
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: FLOW_GREEN, fontSize: 10.5 }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: FLOW_GREEN, boxShadow: `0 0 8px ${FLOW_GREEN}`, animation: "flowPulseDot 1.6s ease-in-out infinite" }} /> LIVE
              </span>
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6, fontSize: 13, color: "#E5E7EB" }}>
              {["Submitted to Google", "Indexed faster", "Connected to AI engines", "Live for customers"].map((t) => (
                <li key={t} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ color: FLOW_BLUE, fontWeight: 800 }}>✓</span> {t}
                </li>
              ))}
            </ul>
          </div>
        </>
      ),
    },
    {
      num: "04",
      color: FLOW_AMBER,
      icon: <span style={{ fontSize: 22 }}>📞</span>,
      title: "Traffic → Customers",
      desc: "Google finds you. Real customers call. Revenue grows. Every single day.",
      visual: (
        <>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 6 }}>Traffic Generated</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 32, fontWeight: 800, color: "#fff", letterSpacing: "-.02em" }}>2,847</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: FLOW_AMBER }}>+327</span>
            </div>
            <Sparkline color={FLOW_AMBER} seed={4} />
          </div>
          <div style={{ flex: 1.1, minWidth: 0 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".16em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 10 }}>Results</div>
            <div style={{ display: "grid", gap: 6, fontSize: 13, color: "#E5E7EB" }}>
              {[
                { label: "Calls", value: "214", delta: "+28" },
                { label: "Website Visits", value: "1,672", delta: "+196" },
                { label: "Revenue", value: "$18,740", delta: "+2,140" },
              ].map((r) => (
                <div key={r.label} style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 12, alignItems: "center" }}>
                  <span style={{ color: "#9CA3AF" }}>{r.label}</span>
                  <span style={{ fontWeight: 700, color: "#fff" }}>{r.value}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: FLOW_AMBER, background: `${FLOW_AMBER}15`, padding: "2px 8px", borderRadius: 6 }}>{r.delta}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ),
    },
  ];

  const trust = [
    { icon: "🛡️", title: "100% Automated", sub: "No manual work ever", color: FLOW_GREEN },
    { icon: "⚡", title: "Runs Every Day", sub: "Morning deployment", color: FLOW_PURPLE },
    { icon: "🎯", title: "Built for Local Wins", sub: "Dominate your market", color: FLOW_BLUE },
    { icon: "🔒", title: "Safe. Smart. Scalable.", sub: "Always up. Always working.", color: FLOW_AMBER },
  ];

  return (
    <section style={{ background: "#000", padding: "80px 20px", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div style={{ position: "relative", marginBottom: 56, overflow: "hidden", borderRadius: 24, border: "1px solid rgba(255,255,255,0.06)", background: "radial-gradient(900px 320px at 80% 30%, rgba(124,58,237,0.10), transparent 60%), radial-gradient(700px 260px at 10% 80%, rgba(16,185,129,0.08), transparent 60%), #050505", padding: "44px 36px 40px" }}>
          {/* Giant faded background word */}
          <div aria-hidden style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 24, pointerEvents: "none", overflow: "hidden" }}>
            <span style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: "clamp(120px, 18vw, 240px)",
              fontWeight: 900,
              letterSpacing: "-0.05em",
              lineHeight: 0.85,
              whiteSpace: "nowrap",
              background: "linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.01) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0 0 60px rgba(124,58,237,0.06)",
            }}>DEPLOYED</span>
          </div>

          {/* Floating keywords (desktop only) */}
          <div aria-hidden className="flow-floaters" style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
            {[
              { t: "barbershop near me", top: "14%", left: "58%", delay: "0s" },
              { t: "best coffee shop", top: "70%", left: "62%", delay: "1.4s" },
              { t: "personal trainer", top: "32%", left: "78%", delay: "2.6s" },
              { t: "web design studio", top: "82%", left: "48%", delay: "3.8s" },
              { t: "luxury barber", top: "8%", left: "82%", delay: "0.7s" },
              { t: "sushi restaurant", top: "50%", left: "88%", delay: "2.1s" },
            ].map((k) => (
              <span key={k.t} style={{
                position: "absolute",
                top: k.top,
                left: k.left,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                color: "rgba(16,185,129,0.45)",
                background: "rgba(16,185,129,0.06)",
                border: "1px solid rgba(16,185,129,0.18)",
                padding: "3px 9px",
                borderRadius: 999,
                whiteSpace: "nowrap",
                animation: `flowFloat 8s ease-in-out ${k.delay} infinite`,
              }}>{k.t}</span>
            ))}
          </div>

          <div style={{ position: "relative", zIndex: 2, maxWidth: 720 }}>
            {/* Deploying live badge — inline, above heading, no overlap */}
            <div className="flow-deploy-badge" style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "6px 12px", borderRadius: 999, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", fontSize: 11, fontWeight: 700, letterSpacing: ".14em", color: FLOW_GREEN, textTransform: "uppercase", marginBottom: 18 }}>
              <span style={{ width: 6, height: 6, borderRadius: 999, background: FLOW_GREEN, boxShadow: `0 0 10px ${FLOW_GREEN}`, animation: "flowPulseDot 1.6s ease-in-out infinite" }} />
              Deploying live
            </div>
            <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: ".18em", color: FLOW_GREEN, textTransform: "uppercase", marginBottom: 14 }}>
              Our System. Fully Automatic.
            </div>
            <h2 style={{ fontSize: "clamp(32px, 5.4vw, 60px)", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.025em", color: "#fff", margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Your keywords.<br />
              <span style={{ background: `linear-gradient(90deg, #fff 0%, ${FLOW_PURPLE} 60%, ${FLOW_GREEN} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Deployed. Daily.</span>
            </h2>
            <p style={{ marginTop: 16, fontSize: 16, lineHeight: 1.6, color: "#9CA3AF", maxWidth: 560 }}>
              From keyword detection to live pages — everything runs automatically.{" "}
              <span style={{ color: FLOW_GREEN }}>Zero manual work required.</span>
            </p>
          </div>
        </div>

        <div className="flow-grid">
          {stages.map((s, i) => (
            <div key={s.num} className="flow-row" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 80px minmax(0,1.35fr)", gap: 20, alignItems: "stretch", marginBottom: 22 }}>
              <StageCard s={s} />
              <div style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 999,
                    border: `1.5px solid ${s.color}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    fontWeight: 800,
                    color: s.color,
                    background: "rgba(0,0,0,0.6)",
                    boxShadow: `0 0 24px ${s.color}55, inset 0 0 14px ${s.color}22`,
                    zIndex: 2,
                  }}
                >
                  {s.num}
                </div>
                {i < stages.length - 1 && (
                  <div style={{ position: "absolute", top: 56, bottom: -22, left: "50%", width: 2, transform: "translateX(-50%)", background: `linear-gradient(180deg, ${s.color}99, ${stages[i + 1].color}55)`, opacity: 0.55, overflow: "hidden" }}>
                    <span style={{ position: "absolute", left: -3, width: 8, height: 8, borderRadius: 999, background: s.color, boxShadow: `0 0 12px ${s.color}`, animation: "flowBeam 2.6s linear infinite", animationDelay: `${i * 0.4}s` }} />
                  </div>
                )}
              </div>
              <VisualPanel s={s}>{s.visual}</VisualPanel>
            </div>
          ))}
        </div>

        <div
          className="flow-trust-bar"
          style={{
            marginTop: 32,
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0,1fr))",
            gap: 18,
            padding: "22px 26px",
            borderRadius: 16,
            background: "linear-gradient(160deg, rgba(15,18,30,0.7), rgba(6,8,16,0.88))",
            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 24px 60px -30px rgba(0,0,0,0.7)",
          }}
        >
          {trust.map((t) => (
            <div key={t.title} style={{ display: "flex", gap: 14, alignItems: "center" }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: `radial-gradient(circle at 30% 30%, ${t.color}33, ${t.color}08 70%, transparent)`,
                  border: `1px solid ${t.color}55`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                  boxShadow: `0 0 16px ${t.color}30`,
                  flexShrink: 0,
                }}
              >
                {t.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{t.title}</div>
                <div style={{ fontSize: 12, color: "#9CA3AF" }}>{t.sub}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes flowBeam {
          0% { top: -10px; opacity: 0; }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        @keyframes flowPulseDot {
          0%,100% { opacity: 1; transform: scale(1); }
          50% { opacity: .55; transform: scale(1.4); }
        }
        @keyframes flowFloat {
          0%,100% { transform: translateY(0) translateX(0); opacity: .35; }
          50% { transform: translateY(-12px) translateX(6px); opacity: .7; }
        }
        @media (max-width: 900px) {
          .flow-floaters { display: none !important; }
        }
        .flow-stage-card:hover {
          transform: translateY(-2px);
        }
        @media (max-width: 900px) {
          .flow-row {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }
          .flow-row > div:nth-child(2) {
            display: none !important;
          }
          .flow-trust-bar {
            grid-template-columns: repeat(2, minmax(0,1fr)) !important;
          }
        }
        @media (max-width: 520px) {
          .flow-trust-bar {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

// ─── OrbitRank Control Center ─────────────
const ControlCenterSection = () => {
  // Activity nodes positioned around the brain (label cards)
  const activityNodes = [
    { t: "Schema Repaired",          x: 28, y: 22, c: "#10b981", icon: "❄" },
    { t: "AI Mention Detected",      x: 50, y: 14, c: "#3b82f6", icon: "◈" },
    { t: "Knowledge Graph Updated",  x: 72, y: 22, c: "#a855f7", icon: "✦" },
    { t: "Citation Validated",       x: 18, y: 34, c: "#3b82f6", icon: "ℹ" },
    { t: "AI Crawl Detected",        x: 82, y: 34, c: "#a855f7", icon: "◬" },
    { t: "Internal Links Connected", x: 18, y: 48, c: "#10b981", icon: "⤳" },
    { t: "Technical Issue Repaired", x: 82, y: 48, c: "#10b981", icon: "⛨" },
    { t: "Visibility Signal Boosted",x: 18, y: 62, c: "#10b981", icon: "▲" },
    { t: "Content Optimized",        x: 82, y: 62, c: "#3b82f6", icon: "❖" },
    { t: "Entity Alignment Complete",x: 22, y: 78, c: "#10b981", icon: "◉" },
    { t: "AI Trust Score Improved",  x: 78, y: 78, c: "#3b82f6", icon: "★" },
    { t: "Local Relevance Updated",  x: 32, y: 88, c: "#a855f7", icon: "◐" },
    { t: "Deployment Successful",    x: 68, y: 88, c: "#10b981", icon: "▣" },
    { t: "Google Index Synced",      x: 50, y: 94, c: "#3b82f6", icon: "◎" },
  ];

  // Beams from each node to the central brain
  const beams = activityNodes.map(n => ({ x1: n.x, y1: n.y, x2: 50, y2: 52 }));

  // Perimeter signal nodes (small glowing dots between cards)
  const sparks = [
    { x: 38, y: 28 }, { x: 62, y: 28 }, { x: 30, y: 40 }, { x: 70, y: 40 },
    { x: 28, y: 56 }, { x: 72, y: 56 }, { x: 32, y: 70 }, { x: 68, y: 70 },
    { x: 44, y: 86 }, { x: 56, y: 86 }, { x: 50, y: 24 }, { x: 50, y: 80 },
  ];

  const liveFeed = [
    { t: "AI mention detected",     a: "2s ago",  c: "#3b82f6" },
    { t: "Schema repaired",         a: "5s ago",  c: "#10b981" },
    { t: "Citation validated",      a: "8s ago",  c: "#a855f7" },
    { t: "Page deployed",           a: "12s ago", c: "#10b981" },
    { t: "Internal links connected",a: "14s ago", c: "#10b981" },
    { t: "Index synced",            a: "18s ago", c: "#3b82f6" },
    { t: "Visibility boosted",      a: "22s ago", c: "#10b981" },
  ];

  const systemStatus = [
    { t: "AI Engines",   v: "Operational", c: "#10b981" },
    { t: "Monitoring",   v: "Active",      c: "#10b981" },
    { t: "Automations",  v: "Running",     c: "#10b981" },
    { t: "Repairs",      v: "Completed",   c: "#10b981" },
    { t: "Deployments",  v: "Live",        c: "#10b981" },
    { t: "Governance",   v: "Active",      c: "#10b981" },
  ];

  const liveImpact = [
    { t: "AI Mentions",       v: "+247"   },
    { t: "Citations",         v: "+312"   },
    { t: "Visibility Signals",v: "+1,128" },
    { t: "Indexed Pages",     v: "+96"    },
    { t: "Deployed Pages",    v: "+24"    },
  ];

  const features = [
    { t: "Intelligent Control",   d: "AI makes the right decisions",        c: "#10b981", i: "🧠" },
    { t: "Automatic Execution",   d: "Repairs, optimizes, deploys",         c: "#a855f7", i: "⚡" },
    { t: "Real-Time Governance",  d: "Everything monitored 24/7",           c: "#3b82f6", i: "🛡" },
    { t: "Visibility Mastery",    d: "AI sees you. Customers find you.",    c: "#10b981", i: "◎" },
  ];

  const panelStyle: React.CSSProperties = {
    background: "linear-gradient(180deg, rgba(10,14,18,0.85), rgba(6,9,12,0.85))",
    border: "1px solid rgba(16,185,129,0.14)",
    borderRadius: 16,
    padding: 18,
    boxShadow: "0 20px 60px -30px rgba(16,185,129,0.25), inset 0 1px 0 rgba(255,255,255,0.03)",
    backdropFilter: "blur(8px)",
  };
  const panelLabel: React.CSSProperties = {
    display: "flex", alignItems: "center", gap: 8,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: 10, letterSpacing: ".22em", textTransform: "uppercase",
    color: "rgba(255,255,255,0.55)", marginBottom: 14,
  };

  return (
    <section style={{
      position: "relative",
      padding: "120px 24px 140px",
      background: "#000000",
      overflow: "hidden",
    }}>
      {/* ambient grid */}
      <div aria-hidden style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
        maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        pointerEvents: "none",
      }} />

      {/* Header */}
      <div style={{ maxWidth: 1200, margin: "0 auto", position: "relative", zIndex: 2, textAlign: "center" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: "rgba(16,185,129,0.08)",
          border: "1px solid rgba(16,185,129,0.25)",
          fontSize: 10.5, fontWeight: 700, letterSpacing: ".22em",
          color: "#10b981", textTransform: "uppercase",
          boxShadow: "0 0 24px rgba(16,185,129,0.18)",
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: "50%", background: "#10b981",
            boxShadow: "0 0 10px #10b981",
            animation: "ccPulseDot 1.8s ease-in-out infinite",
          }} />
          OrbitRank Control Center
        </div>
        <h2 style={{
          fontSize: "clamp(34px, 5vw, 64px)", fontWeight: 800,
          letterSpacing: "-0.025em", lineHeight: 1.05,
          margin: "22px 0 18px",
          background: "linear-gradient(180deg, #ffffff 0%, #b8c2cc 100%)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}>
          The brain behind every AI decision.
        </h2>
        <p style={{
          fontSize: "clamp(15px, 1.4vw, 18px)", color: "#8a96a3",
          maxWidth: 760, margin: "0 auto", lineHeight: 1.6,
        }}>
          OrbitRank continuously monitors, repairs, deploys, automates, and governs AI visibility — in real time.
        </p>
      </div>

      {/* 3-column control center */}
      <div className="cc-grid" style={{
        maxWidth: 1400, margin: "56px auto 0",
        display: "grid",
        gridTemplateColumns: "260px 1fr 260px",
        gap: 20,
        position: "relative", zIndex: 2,
      }}>
        {/* LEFT — System Status + Live Feed */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={panelStyle}>
            <div style={panelLabel}>
              <span style={{ width: 6, height: 6, borderRadius: 50, background: "#10b981", boxShadow: "0 0 8px #10b981", animation: "ccPulseDot 1.8s ease-in-out infinite" }} />
              System Status
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {systemStatus.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: 8,
                    background: "rgba(16,185,129,0.10)",
                    border: "1px solid rgba(16,185,129,0.25)",
                    display: "grid", placeItems: "center",
                    color: "#10b981", fontSize: 12,
                  }}>◉</div>
                  <div style={{ flex: 1, lineHeight: 1.2 }}>
                    <div style={{ fontSize: 12.5, color: "#e6ecf2", fontWeight: 500 }}>{s.t}</div>
                    <div style={{ fontSize: 11, color: s.c, fontWeight: 600 }}>{s.v}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={panelStyle}>
            <div style={panelLabel}>Live Activity Feed</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {liveFeed.map((f, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  fontSize: 11.5,
                  animation: "ccFeedIn 0.6s ease-out both",
                  animationDelay: `${i * 0.08}s`,
                }}>
                  <span style={{
                    width: 6, height: 6, borderRadius: 50, background: f.c,
                    boxShadow: `0 0 8px ${f.c}`,
                    animation: "ccPulseDot 2s ease-in-out infinite",
                    animationDelay: `${i * 0.15}s`,
                  }} />
                  <span style={{ flex: 1, color: "#cdd6df" }}>{f.t}</span>
                  <span style={{ color: "#5c6773", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10 }}>{f.a}</span>
                </div>
              ))}
              <div style={{
                marginTop: 6, paddingTop: 10,
                borderTop: "1px solid rgba(255,255,255,0.05)",
                display: "flex", alignItems: "center", gap: 8,
                fontSize: 11, color: "#7d8a96",
              }}>
                <span style={{ width: 6, height: 6, borderRadius: 50, background: "#10b981", boxShadow: "0 0 8px #10b981", animation: "ccPulseDot 1.6s ease-in-out infinite" }} />
                All systems operating normally
              </div>
            </div>
          </div>
        </div>

        {/* CENTER — Cinematic brain visual */}
        <div style={{
          position: "relative",
          minHeight: 720,
          borderRadius: 24,
          border: "1px solid rgba(255,255,255,0.06)",
          background: "radial-gradient(ellipse at 50% 50%, rgba(16,185,129,0.10), transparent 55%), linear-gradient(180deg, #07090c 0%, #04060a 100%)",
          overflow: "hidden",
          boxShadow: "0 40px 120px -40px rgba(16,185,129,0.35), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}>
          {/* corner ticks */}
          {[[8,8],[8,"calc(100% - 14px)"],["calc(100% - 14px)",8],["calc(100% - 14px)","calc(100% - 14px)"]].map((pos,i)=>(
            <div key={i} aria-hidden style={{
              position: "absolute", left: pos[0] as any, top: pos[1] as any,
              width: 8, height: 8, borderLeft: "1px solid rgba(16,185,129,0.4)", borderTop: "1px solid rgba(16,185,129,0.4)",
            }} />
          ))}

          {/* SVG network */}
          <svg viewBox="0 0 100 100" preserveAspectRatio="none"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
            <defs>
              <radialGradient id="cc-core" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#10b981" stopOpacity="1" />
                <stop offset="35%" stopColor="#10b981" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="cc-halo" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.3" />
                <stop offset="60%" stopColor="#10b981" stopOpacity="0.05" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
              </radialGradient>
              <linearGradient id="cc-beam" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0" />
                <stop offset="50%" stopColor="#10b981" stopOpacity="0.95" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
              </linearGradient>
              <filter id="cc-glow"><feGaussianBlur stdDeviation="0.6" /></filter>
            </defs>

            {/* outer halo */}
            <circle cx="50" cy="52" r="42" fill="url(#cc-halo)" />

            {/* concentric rings */}
            {[16, 24, 32, 40].map((r, i) => (
              <circle key={i} cx="50" cy="52" r={r} fill="none"
                stroke="rgba(16,185,129,0.20)" strokeWidth="0.12"
                strokeDasharray="0.6 1.4"
                style={{ transformOrigin: "50px 52px", animation: `ccRingSpin ${30 + i * 12}s linear infinite ${i % 2 ? "reverse" : ""}` }} />
            ))}

            {/* connection beams (node → brain) */}
            {beams.map((b, i) => (
              <g key={i}>
                <line x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2}
                  stroke="rgba(16,185,129,0.18)" strokeWidth="0.12" />
                <line x1={b.x1} y1={b.y1} x2={b.x2} y2={b.y2}
                  stroke="url(#cc-beam)" strokeWidth="0.28"
                  strokeDasharray="2 14" pathLength={16}
                  style={{ animation: `ccBeamFlow 4.2s linear infinite`, animationDelay: `${i * 0.28}s` }} />
              </g>
            ))}

            {/* perimeter sparks */}
            {sparks.map((n, i) => (
              <g key={i}>
                <circle cx={n.x} cy={n.y} r="0.6" fill="#10b981" opacity="0.85"
                  style={{ animation: `ccNodePulse 2.4s ease-in-out infinite`, animationDelay: `${i * 0.18}s`, transformOrigin: `${n.x}px ${n.y}px` }} />
                <circle cx={n.x} cy={n.y} r="1.6" fill="none" stroke="rgba(16,185,129,0.4)" strokeWidth="0.07"
                  style={{ animation: `ccNodeRing 2.4s ease-out infinite`, animationDelay: `${i * 0.18}s`, transformOrigin: `${n.x}px ${n.y}px` }} />
              </g>
            ))}

            {/* central brain core glow */}
            <circle cx="50" cy="52" r="14" fill="url(#cc-core)"
              style={{ animation: "ccCoreBreathe 4s ease-in-out infinite" }} />
            <circle cx="50" cy="52" r="6" fill="url(#cc-core)" opacity="0.9"
              style={{ animation: "ccCoreBreathe 3s ease-in-out infinite reverse" }} />
          </svg>

          {/* Premium neural AI brain (same as AI Insight, tinted green) */}
          <div style={{
            position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            width: "min(23%, 190px)", aspectRatio: "1",
            pointerEvents: "none",
            animation: "ccBrainPulse 4.2s ease-in-out infinite",
            display: "grid", placeItems: "center",
          }}>
            <div style={{
              position: "absolute", inset: "10%",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(16,185,129,0.35) 0%, rgba(16,185,129,0.08) 50%, transparent 75%)",
              filter: "blur(8px)",
            }} />
            <img
              src={brainGreenImg}
              alt="AI brain"
              style={{
                position: "relative",
                width: "100%", height: "100%",
                objectFit: "contain",
                filter: "drop-shadow(0 0 28px rgba(16,185,129,0.6)) drop-shadow(0 0 60px rgba(16,185,129,0.25))",
              }}
            />
          </div>

          {/* Center label */}
          <div style={{
            position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, 130%)",
            textAlign: "center",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            color: "rgba(220,255,240,0.95)",
            textShadow: "0 0 18px rgba(16,185,129,0.7)",
            pointerEvents: "none",
            zIndex: 3,
            whiteSpace: "nowrap",
          }}>
            <div style={{ fontSize: 10, letterSpacing: ".34em", color: "rgba(16,185,129,0.95)" }}>ORBITRANK</div>
            <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: ".22em", marginTop: 4 }}>AI CONTROL CENTER</div>
          </div>

          {/* Floating activity cards (positioned around brain) */}
          <div className="cc-floating-cards">
          {activityNodes.map((n, i) => (
            <div key={i} style={{
              position: "absolute", left: `${n.x}%`, top: `${n.y}%`,
              transform: "translate(-50%, -50%)",
              padding: "8px 12px", borderRadius: 10,
              background: "rgba(8,12,16,0.82)",
              border: `1px solid ${n.c}55`,
              boxShadow: `0 0 24px ${n.c}25, inset 0 1px 0 rgba(255,255,255,0.04)`,
              backdropFilter: "blur(6px)",
              display: "flex", alignItems: "center", gap: 8,
              whiteSpace: "nowrap",
              animation: "ccCardFloat 6s ease-in-out infinite",
              animationDelay: `${i * 0.3}s`,
              pointerEvents: "none",
              zIndex: 4,
            }}>
              <span style={{
                width: 22, height: 22, borderRadius: 6,
                background: `${n.c}1a`, color: n.c,
                display: "grid", placeItems: "center",
                fontSize: 12, border: `1px solid ${n.c}40`,
              }}>{n.icon}</span>
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ fontSize: 11.5, color: "#eef3f8", fontWeight: 600 }}>{n.t}</div>
                <div style={{ fontSize: 9, letterSpacing: ".2em", color: n.c, fontWeight: 700 }}>
                  <span style={{
                    display: "inline-block", width: 5, height: 5, borderRadius: 50,
                    background: n.c, marginRight: 5, verticalAlign: "middle",
                    boxShadow: `0 0 6px ${n.c}`,
                    animation: "ccPulseDot 1.6s ease-in-out infinite",
                  }} />
                  LIVE
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* RIGHT — AI Visibility + Live Impact + Intelligence */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={panelStyle}>
            <div style={panelLabel}>
              <span style={{ width: 6, height: 6, borderRadius: 50, background: "#10b981", boxShadow: "0 0 8px #10b981", animation: "ccPulseDot 1.8s ease-in-out infinite" }} />
              AI Visibility Overview
            </div>
            <div style={{ position: "relative", width: 160, height: 160, margin: "4px auto 0" }}>
              <svg viewBox="0 0 100 100" style={{ width: "100%", height: "100%", transform: "rotate(-90deg)" }}>
                <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="6" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#10b981" strokeWidth="6"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 42 * 0.96} ${2 * Math.PI * 42}`}
                  style={{ filter: "drop-shadow(0 0 6px rgba(16,185,129,0.8))" }} />
              </svg>
              <div style={{
                position: "absolute", inset: 0, display: "grid", placeItems: "center",
                textAlign: "center",
              }}>
                <div>
                  <div style={{ fontSize: 30, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}>96%</div>
                  <div style={{ fontSize: 10, color: "#7d8a96", marginTop: 2 }}>AI Visibility Score</div>
                </div>
              </div>
            </div>
            <div style={{ textAlign: "center", color: "#10b981", fontSize: 12, fontWeight: 700, marginTop: 6 }}>+8%</div>
          </div>

          <div style={panelStyle}>
            <div style={panelLabel}>Live Impact</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {liveImpact.map((m, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12 }}>
                  <span style={{ color: "#cdd6df", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 4, height: 4, borderRadius: 50, background: "#10b981", boxShadow: "0 0 6px #10b981" }} />
                    {m.t}
                  </span>
                  <span style={{ color: "#10b981", fontWeight: 700, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>{m.v}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={panelStyle}>
            <div style={panelLabel}>System Intelligence</div>
            <p style={{ fontSize: 12, color: "#9aa6b2", lineHeight: 1.55, margin: 0 }}>
              OrbitRank's AI brain analyzes, decides, and executes thousands of optimizations every minute to ensure AI platforms receive the right signals about your business.
            </p>
            <div style={{
              marginTop: 12, display: "flex", alignItems: "center", gap: 8,
              fontSize: 11, color: "#10b981", fontWeight: 600,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: 50, background: "#10b981", boxShadow: "0 0 8px #10b981", animation: "ccPulseDot 1.6s ease-in-out infinite" }} />
              All systems intelligent
            </div>
          </div>
        </div>
      </div>

      {/* Bottom — 4 premium feature blocks */}
      <div style={{
        maxWidth: 1400, margin: "28px auto 0",
        background: "linear-gradient(180deg, rgba(10,14,18,0.85), rgba(6,9,12,0.85))",
        border: "1px solid rgba(255,255,255,0.06)",
        borderRadius: 18, padding: "22px 24px",
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: 18,
        boxShadow: "0 30px 80px -40px rgba(16,185,129,0.25)",
      }}>
        {features.map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 12,
              background: `${f.c}14`, border: `1px solid ${f.c}40`,
              color: f.c, display: "grid", placeItems: "center",
              fontSize: 20,
              boxShadow: `0 0 22px ${f.c}25`,
            }}>{f.i}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#f3f5f7", letterSpacing: "-0.01em" }}>{f.t}</div>
              <div style={{ fontSize: 12.5, color: "#7d8a96", marginTop: 2 }}>{f.d}</div>
            </div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes ccPulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(0.85); }
        }
        @keyframes ccRingSpin { to { transform: rotate(360deg); } }
        @keyframes ccBeamFlow {
          0% { stroke-dashoffset: 16; opacity: 0.2; }
          50% { opacity: 1; }
          100% { stroke-dashoffset: -16; opacity: 0.2; }
        }
        @keyframes ccNodePulse {
          0%, 100% { opacity: 0.5; transform: scale(0.85); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes ccNodeRing {
          0% { opacity: 0.7; transform: scale(0.4); }
          100% { opacity: 0; transform: scale(1.8); }
        }
        @keyframes ccCoreBreathe {
          0%, 100% { opacity: 0.9; }
          50% { opacity: 1; }
        }
        @keyframes ccBrainPulse {
          0%, 100% { opacity: 0.94; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 1; transform: translate(-50%, -50%) scale(1.035); }
        }
        @keyframes ccCardFloat {
          0%, 100% { transform: translate(-50%, -50%) translateY(0); }
          50% { transform: translate(-50%, -50%) translateY(-4px); }
        }
        @keyframes ccFeedIn {
          from { opacity: 0; transform: translateX(-6px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @media (max-width: 1100px) {
          .cc-grid { grid-template-columns: 1fr !important; }
          .cc-grid > div:nth-child(2) { min-height: 560px !important; order: -1; }
        }
      `}</style>
    </section>
  );
};

const ResultsSection = ControlCenterSection;

// ─── How It Works ─────────────────────────
const HOW_STEPS = [
  { label: "Connect your website", caption: "Secure handshake with OrbitRank" },
  { label: "AI scans your market", caption: "Detecting high-intent keywords" },
  { label: "Content deploys automatically", caption: "New pages publish & index" },
  { label: "Visibility grows", caption: "AI search presence expands" },
  { label: "Customers arrive", caption: "Bookings, leads, reservations" },
];

const MARKET_KEYWORDS = [
  "best coffee shop downtown",
  "luxury hair salon",
  "fitness studio membership",
  "boutique hotel",
  "creative marketing agency",
  "best sushi restaurant",
];

const HowItWorks = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % HOW_STEPS.length), 4200);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="how-it-works" className="lo-how-section lo-how-cinema">
      <div className="lo-how-grid">
        {/* LEFT */}
        <div className="lo-how-left">
          <span className="lo-section-label">How It Works</span>
          <h2 className="lo-section-title">Your website grows.<br />While you sleep ✨</h2>
          <p className="lo-section-sub">
            Connect your website once. OrbitRank quietly handles the growth — scanning your market, deploying pages, and bringing customers in. Automatically.
          </p>
          <div className="lo-how-stepper">
            {HOW_STEPS.map((s, i) => (
              <div key={i} className={`lo-how-stepitem ${i === step ? "is-active" : ""} ${i < step ? "is-done" : ""}`}>
                <span className="lo-how-stepdot" />
                <div>
                  <div className="lo-how-steplabel">{s.label}</div>
                  <div className="lo-how-stepcaption">{s.caption}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT — Cinematic stage */}
        <div className="lo-how-stage">
          <div className="lo-how-glow" aria-hidden />
          <div className="lo-how-grid-bg" aria-hidden />
          <div className="lo-how-scanline" aria-hidden />
          <div className="lo-how-vignette" aria-hidden />

          {/* Ambient particles */}
          <div className="lo-how-particles" aria-hidden>
            {Array.from({ length: 22 }).map((_, i) => (
              <span
                key={i}
                style={{
                  left: `${(i * 37) % 100}%`,
                  top: `${(i * 53) % 100}%`,
                  animationDelay: `${(i % 8) * 0.7}s`,
                  animationDuration: `${6 + (i % 5)}s`,
                }}
              />
            ))}
          </div>

          {/* Floating growth indicators */}
          <div className="lo-how-chips" aria-hidden>
            <span className="lo-how-chip is-purple" style={{ left: "6%", top: "20%", animationDelay: "0s" }}>+ visibility</span>
            <span className="lo-how-chip is-cyan" style={{ right: "8%", top: "28%", animationDelay: "1.1s" }}>citation detected</span>
            <span className="lo-how-chip" style={{ left: "10%", bottom: "22%", animationDelay: "2.2s" }}>indexed ✓</span>
            <span className="lo-how-chip is-gold" style={{ right: "6%", bottom: "30%", animationDelay: "3.3s" }}>★ new customer</span>
            <span className="lo-how-chip is-blue" style={{ left: "44%", top: "12%", animationDelay: "4.4s" }}>traffic ↑</span>
          </div>

          {/* Micro-notification ticker */}
          <div className="lo-how-microticker" aria-hidden>
            <span className="lo-how-micro is-purple" style={{ animationDelay: "0s" }}>● AI visibility improved · +12%</span>
            <span className="lo-how-micro is-green" style={{ animationDelay: "3s" }}>● new customer · just booked</span>
            <span className="lo-how-micro is-cyan" style={{ animationDelay: "6s" }}>● citation detected · forbes.com</span>
            <span className="lo-how-micro is-gold" style={{ animationDelay: "9s" }}>● traffic increasing · +184%</span>
          </div>

          {/* Top status bar */}
          <div className="lo-how-topbar">
            <span className="lo-how-livedot" />
            <span className="lo-how-toptext">orbitrank · live</span>
            <span className="lo-how-stepbadge">Step {step + 1} / 5</span>
          </div>

          {/* Scenes */}
          <div className="lo-how-scene-wrap">
            {/* STEP 1 — Connect */}
            <div className={`lo-how-scene ${step === 0 ? "is-on" : ""}`}>
              <div className="lo-how-connect">
                <div className="lo-how-node lo-how-node-site">
                  <div className="lo-how-url">yourwebsite.com</div>
                </div>
                <div className="lo-how-wire"><span /></div>
                <div className="lo-how-node lo-how-node-orbit">
                  <img src={logoMarkTransparent} alt="" className="lo-how-orbit-logo" />
                </div>
              </div>
              <div className="lo-how-status">
                <span className="lo-how-check">✓</span> Connected securely
              </div>
            </div>

            {/* STEP 2 — Scan */}
            <div className={`lo-how-scene ${step === 1 ? "is-on" : ""}`}>
              <div className="lo-how-scan">
                <div className="lo-how-radar"><span /><span /><span /></div>
                <div className="lo-how-keywords">
                  {MARKET_KEYWORDS.map((k, i) => (
                    <span key={k} className="lo-how-kw" style={{ animationDelay: `${i * 0.35}s` }}>{k}</span>
                  ))}
                </div>
              </div>
              <div className="lo-how-status"><span className="lo-how-check">◎</span> AI detecting high-intent keywords</div>
            </div>

            {/* STEP 3 — Deploy */}
            <div className={`lo-how-scene ${step === 2 ? "is-on" : ""}`}>
              <div className="lo-how-deploy">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="lo-how-page" style={{ animationDelay: `${i * 0.35}s` }}>
                    <div className="lo-how-page-bar" />
                    <div className="lo-how-page-line" />
                    <div className="lo-how-page-line short" />
                    <div className="lo-how-page-tag">✓ Published</div>
                  </div>
                ))}
              </div>
              <div className="lo-how-status"><span className="lo-how-check">✓</span> Pages deployed & indexed</div>
            </div>

            {/* STEP 4 — Visibility */}
            <div className={`lo-how-scene ${step === 3 ? "is-on" : ""}`}>
              <div className="lo-how-vis">
                <svg viewBox="0 0 320 140" className="lo-how-graph" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="howGradFill" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#a78bfa" stopOpacity="0.35" />
                      <stop offset="50%" stopColor="#10b981" stopOpacity="0.30" />
                      <stop offset="100%" stopColor="#fbbf24" stopOpacity="0.28" />
                    </linearGradient>
                    <linearGradient id="howGradLine" x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0%" stopColor="#a78bfa" />
                      <stop offset="40%" stopColor="#67e8f9" />
                      <stop offset="70%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#fbbf24" />
                    </linearGradient>
                    <linearGradient id="howGradFade" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#10b981" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </linearGradient>
                    <filter id="howGlowF" x="-10%" y="-30%" width="120%" height="160%">
                      <feGaussianBlur stdDeviation="3" result="b" />
                      <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                    </filter>
                  </defs>
                  <path d="M0,120 C40,110 70,95 110,80 C150,65 190,55 230,38 C260,26 290,18 320,10 L320,140 L0,140 Z" fill="url(#howGradFill)" />
                  <path d="M0,120 C40,110 70,95 110,80 C150,65 190,55 230,38 C260,26 290,18 320,10 L320,140 L0,140 Z" fill="url(#howGradFade)" />
                  <path d="M0,120 C40,110 70,95 110,80 C150,65 190,55 230,38 C260,26 290,18 320,10" fill="none" stroke="url(#howGradLine)" strokeWidth="2.5" strokeLinecap="round" filter="url(#howGlowF)" />
                  <circle r="4" fill="#fbbf24" filter="url(#howGlowF)">
                    <animateMotion dur="6s" repeatCount="indefinite" path="M0,120 C40,110 70,95 110,80 C150,65 190,55 230,38 C260,26 290,18 320,10" />
                  </circle>
                </svg>
                <div className="lo-how-vis-score">
                  <div className="lo-how-vis-num">+184%</div>
                  <div className="lo-how-vis-cap">AI visibility this month</div>
                </div>
              </div>
              <div className="lo-how-status"><span className="lo-how-check">✓</span> AI visibility improved</div>
            </div>

            {/* STEP 5 — Customers */}
            <div className={`lo-how-scene ${step === 4 ? "is-on" : ""}`}>
              <div className="lo-how-leads">
                {[
                  { t: "New booking", s: "Table for 4 · 8:30 PM", c: "is-green" },
                  { t: "Lead detected", s: "Marketing inquiry · €4.2k", c: "is-purple" },
                  { t: "Reservation confirmed", s: "Boutique hotel · 2 nights", c: "is-cyan" },
                  { t: "Appointment booked", s: "Hair salon · Saturday", c: "is-gold" },
                ].map((l, i) => (
                  <div key={i} className={`lo-how-lead ${l.c}`} style={{ animationDelay: `${i * 0.4}s` }}>
                    <span className="lo-how-lead-dot" />
                    <div>
                      <div className="lo-how-lead-title">{l.t}</div>
                      <div className="lo-how-lead-sub">{l.s}</div>
                    </div>
                    <span className="lo-how-lead-time">just now</span>
                  </div>
                ))}
              </div>
              <div className="lo-how-status"><span className="lo-how-check">✓</span> Real customers arriving</div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "center", marginTop: 36, padding: "0 20px" }}>
        <a
          href="/blog"
          className="how-it-works-learn-cta"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "15px 24px",
            borderRadius: 11,
            background: "transparent",
            border: `1px solid #A78BFA66`,
            color: "#A78BFA",
            fontWeight: 800,
            fontSize: 14,
            cursor: "pointer",
            boxShadow: `0 0 0 1px #A78BFA22 inset, 0 6px 18px #A78BFA1f`,
            textDecoration: "none",
            transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease, color .2s ease",
            maxWidth: "100%",
            whiteSpace: "normal",
            textAlign: "center",
          }}
        >
          See how automated SEO actually works →
        </a>
      </div>
      <style>{`
        .how-it-works-learn-cta:hover {
          transform: translateY(-1px);
          border-color: #A78BFA !important;
          color: #C4B5FD !important;
          box-shadow: 0 0 0 1px #A78BFA44 inset, 0 10px 26px #A78BFA33 !important;
        }
      `}</style>
    </section>
  );
};

// ─── Features ─────────────────────────────
const Features = () => {
  const feats = [
    { Icon: FileText, title: "Daily SEO Pages", desc: "Fresh, optimized pages published every morning.", tint: "#00D97E", bg: "rgba(0,217,126,0.10)" },
    { Icon: BarChart3, title: "Rank Tracking", desc: "See exactly where you appear in Google for every keyword.", tint: "#8B5CF6", bg: "rgba(139,92,246,0.12)" },
    { Icon: Link2, title: "Backlink Building", desc: "Auto-submitted to 20+ high DA directories every day.", tint: "#F97316", bg: "rgba(249,115,22,0.12)" },
    { Icon: Target, title: "Competitor Analysis", desc: "Find gaps in your market and beat your competition.", tint: "#3B82F6", bg: "rgba(59,130,246,0.12)" },
    { Icon: Zap, title: "Speed Monitoring", desc: "Site health and performance tracked every hour.", tint: "#F59E0B", bg: "rgba(245,158,11,0.12)" },
    { Icon: FileBarChart, title: "Monthly AI Report", desc: "Full SEO progress report written by AI, delivered monthly.", tint: "#2DD4BF", bg: "rgba(45,212,191,0.12)" },
  ];

  const heroRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const [progress, setProgress] = useState(0);
  const [pathLen, setPathLen] = useState(0);
  const [point, setPoint] = useState({ x: 50, y: 220 });

  useEffect(() => {
    if (pathRef.current) setPathLen(pathRef.current.getTotalLength());
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(min-width: 768px)").matches) return;
    const el = heroRef.current;
    if (!el) return;
    let raf = 0;
    let started = false;
    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
    const run = () => {
      const duration = 2400;
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const p = easeInOut(t);
        setProgress(p);
        if (pathRef.current) {
          const len = pathRef.current.getTotalLength();
          const pt = pathRef.current.getPointAtLength(len * p);
          setPoint({ x: pt.x, y: pt.y });
        }
        if (t < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && !started) {
          started = true;
          run();
          io.disconnect();
        }
      });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => {
      io.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="features" className="lo-features lo-features-v2" style={{ scrollMarginTop: 120 }}>
      <div className="lo-features-head">

        <div className="lo-features-head-left">
          <span className="lo-section-label">Everything Included</span>
          <h2 className="lo-section-title">Everything a top SEO<br />agency does — <span className="lo-accent">automated.</span></h2>
          <p className="lo-section-sub lo-features-sub">Powerful SEO tools working 24/7 to grow your rankings, traffic, and sales — on autopilot.</p>
        </div>
        <div ref={heroRef} className="lo-features-hero" aria-hidden="true">
          <div className="lo-features-hero-glow" />
          <svg viewBox="0 0 420 280" className="lo-features-hero-svg">
            <defs>
              <linearGradient id="featChartLine" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0%" stopColor="#00D97E" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#00FF99" stopOpacity="1" />
              </linearGradient>
              <linearGradient id="featChartFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#00D97E" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#00D97E" stopOpacity="0" />
              </linearGradient>
            </defs>
            <rect x="20" y="20" width="380" height="240" rx="22" fill="rgba(255,255,255,0.02)" stroke="rgba(255,255,255,0.08)" />
            {[60,100,140,180,220].map(y => (
              <line key={y} x1="40" y1={y} x2="380" y2={y} stroke="rgba(255,255,255,0.04)" />
            ))}
            <path d="M50 220 L100 200 L150 180 L200 150 L250 130 L300 95 L350 65 L380 50 L380 240 L50 240 Z" fill="url(#featChartFill)" />
            <path ref={pathRef} d="M50 220 L100 200 L150 180 L200 150 L250 130 L300 95 L350 65 L380 50" stroke="rgba(0,217,126,0.25)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M50 220 L100 200 L150 180 L200 150 L250 130 L300 95 L350 65 L380 50" stroke="url(#featChartLine)" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray={pathLen || 1} strokeDashoffset={(pathLen || 1) * (1 - progress)} style={{ filter: "drop-shadow(0 0 6px rgba(0,255,153,0.55))", transition: "stroke-dashoffset 0.15s linear" }} />
            <circle cx={point.x} cy={point.y} r="12" fill="#00FF99" opacity="0.22" style={{ transition: "cx 0.15s linear, cy 0.15s linear" }} />
            <circle cx={point.x} cy={point.y} r="6" fill="#00FF99" style={{ filter: "drop-shadow(0 0 8px #00FF99)", transition: "cx 0.15s linear, cy 0.15s linear" }} />

          </svg>
          <div className="lo-features-hero-badge">
            <div className="lo-features-hero-badge-inner">🚀</div>
          </div>
        </div>
      </div>

      <div className="lo-feat-grid">
        {feats.map((f, i) => (
          <div key={i} className="lo-feat-card">
            <div className="lo-feat-card-glow" style={{ background: `radial-gradient(circle at 0% 0%, ${f.bg}, transparent 60%)` }} />
            <div className="lo-feat-card-top">
              <div className="lo-feat-icon-wrap" style={{ background: f.bg, color: f.tint, boxShadow: `0 0 30px -8px ${f.tint}55` }}>
                <f.Icon size={26} strokeWidth={2} />
              </div>
              <div className="lo-feat-card-text">
                <div className="lo-feat-title">{f.title}</div>
                <div className="lo-feat-desc">{f.desc}</div>
              </div>
            </div>
            <div className="lo-feat-card-foot">
              <span className="lo-feat-badge"><span className="lo-feat-badge-dot">✓</span> AUTOMATED</span>
              <ArrowRight size={16} className="lo-feat-arrow" />
            </div>
          </div>
        ))}
      </div>

      <div className="lo-features-cta">
        <div className="lo-features-cta-left">
          <div className="lo-features-cta-icon"><ShieldCheck size={22} /></div>
          <div>
            <div className="lo-features-cta-title">Set it once. We handle the rest.</div>
            <div className="lo-features-cta-sub">Everything runs in the background so you can focus on your business.</div>
          </div>
        </div>
        <a href="/demo" className="lo-features-cta-btn">
          <BarChart3 size={16} /> Activate your SEO
        </a>
      </div>
    </section>
  );
};


// ─── Terminal ─────────────────────────────
const TERMINAL_LINES = [
  { type: "cmd", text: '$ orbitrank deploy --site acme.com' },
  { type: "out", text: '→ scanning keywords...' },
  { type: "out", text: '→ generating page: "best-barbershop-berlin"' },
  { type: "out", text: '→ schema + FAQs added' },
  { type: "out", text: '→ images optimized' },
  { type: "live", text: '✓ page live → acme.com/best-barbershop-berlin' },
  { type: "out", text: '→ submitted to Google Search Console' },
  { type: "live", text: '✓ indexed in 4 hours' },
  { type: "sep", text: '' },
  { type: "cmd", text: '$ orbitrank deploy --site dental-london.com' },
  { type: "out", text: '→ scanning keywords...' },
  { type: "out", text: '→ generating page: "emergency-dentist-near-me"' },
  { type: "out", text: '→ schema + FAQs added' },
  { type: "out", text: '→ images optimized' },
  { type: "live", text: '✓ page live → dental-london.com/emergency-dentist-near-me' },
  { type: "out", text: '→ submitted to Google Search Console' },
  { type: "live", text: '✓ indexed in 3 hours' },
  { type: "sep", text: '' },
  { type: "cmd", text: '$ orbitrank deploy --site paris-coffee.fr' },
  { type: "out", text: '→ scanning keywords...' },
  { type: "out", text: '→ generating page: "meilleur-cafe-paris-15"' },
  { type: "live", text: '✓ page live → paris-coffee.fr/meilleur-cafe-paris-15' },
  { type: "live", text: '✓ indexed in 5 hours' },
];

const TerminalSection = () => {
  const [lines, setLines] = useState<typeof TERMINAL_LINES>([]);
  const [typingText, setTypingText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const idxRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let rafId: number;
    const addLine = () => {
      if (idxRef.current >= TERMINAL_LINES.length) {
        timerRef.current = setTimeout(() => {
          setLines([]);
          idxRef.current = 0;
          schedule();
        }, 2500);
        return;
      }
      const line = TERMINAL_LINES[idxRef.current];
      if (line.type === "cmd") {
        setIsTyping(true);
        let charIdx = 0;
        const typeChar = () => {
          rafId = requestAnimationFrame(() => {
            if (charIdx <= line.text.length) {
              setTypingText(line.text.slice(0, charIdx));
              charIdx++;
              timerRef.current = setTimeout(typeChar, 35);
            } else {
              setIsTyping(false);
              setTypingText("");
              setLines(prev => [...prev.slice(-12), line]);
              idxRef.current++;
              schedule();
            }
          });
        };
        typeChar();
      } else {
        setLines(prev => [...prev.slice(-12), line]);
        idxRef.current++;
        schedule();
      }
    };

    const schedule = () => {
      const line = TERMINAL_LINES[idxRef.current];
      if (!line) return addLine();
      const delay =
        line.type === "cmd" ? 600 :
        line.type === "live" ? 500 :
        line.type === "sep" ? 400 : 250;
      timerRef.current = setTimeout(addLine, delay);
    };

    schedule();
    return () => { clearTimeout(timerRef.current); cancelAnimationFrame(rafId); };
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [lines, typingText]);

  return (
    <>
      {/* Mobile — original terminal */}
      <section className="lo-terminal-section md:hidden">
        <span className="lo-section-label">Daily Deployment</span>
        <h2 className="lo-section-title">Fresh pages.<br />Every morning.</h2>
        <p className="lo-section-sub">While you sleep, OrbitRank deploys a new optimized page to your website. Day after day. Automatically.</p>
        <div className="lo-terminal">
          <div className="lo-terminal-bar">
            <span className="lo-t-dot" style={{ background: "var(--lo-terminal-dot-red)" }} />
            <span className="lo-t-dot" style={{ background: "var(--lo-terminal-dot-yellow)" }} />
            <span className="lo-t-dot" style={{ background: "var(--lo-terminal-dot-green)" }} />
            <span className="lo-t-title">orbitrank — engine v2.1.0</span>
          </div>
          <div ref={scrollRef} className="lo-terminal-body">
            {lines.map((line, i) =>
              line.type === "sep" ? (
                <div key={i} style={{ height: 12 }} />
              ) : (
                <div key={i} className={`lo-t-${line.type}`}>{line.text}</div>
              )
            )}
            {isTyping && (
              <div className="lo-t-cmd">{typingText}<span className="lo-cursor" /></div>
            )}
            {!isTyping && <div><span className="lo-cursor" /></div>}
          </div>
        </div>
      </section>

      {/* Desktop — premium live deployment engine */}
      <TerminalSectionDesktop />
    </>
  );
};

// ─── Fresh Pages — Desktop redesign ───────
const FEED_STEPS = [
  { icon: "gear",   label: "Initializing OrbitRank Engine", t: "00:00:01" },
  { icon: "search", label: "Scanning keywords & opportunities", t: "00:00:02" },
  { icon: "doc",    label: "Generating optimized page", t: "00:00:04" },
  { icon: "code",   label: "Applying schema & FAQs", t: "00:00:06" },
  { icon: "img",    label: "Optimizing images & internal links", t: "00:00:07" },
  { icon: "google", label: "Submitting to Google Search Console", t: "00:00:08" },
  { icon: "check",  label: "Indexed in 4 hours", t: "04:02:17", success: true },
];

const FeedIcon = ({ name }: { name: string }) => {
  const s = { width: 14, height: 14, strokeWidth: 1.8 } as const;
  switch (name) {
    case "gear":   return <Settings {...s} />;
    case "search": return <Search {...s} />;
    case "doc":    return <FileText {...s} />;
    case "code":   return <Code2 {...s} />;
    case "img":    return <ImageIcon {...s} />;
    case "google": return <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 13, lineHeight: 1, color: "#00D97E" }}>G</span>;
    case "check":  return <CheckCircle2 {...s} />;
    default: return null;
  }
};

// ─── Live Engine Visual — premium deployment pipeline ───
const ENGINE_URLS = [
  { d: "acme.com", p: "/best-barbershop-berlin", ms: 142 },
  { d: "dental-london.com", p: "/emergency-dentist-near-me", ms: 168 },
  { d: "paris-coffee.fr", p: "/meilleur-cafe-paris-15", ms: 121 },
  { d: "smile-dental.co", p: "/teeth-whitening-soho", ms: 154 },
  { d: "elite-roofing.uk", p: "/flat-roof-repair-manchester", ms: 189 },
  { d: "yoga-kreuzberg.de", p: "/yoga-classes-mitte", ms: 134 },
  { d: "tokyo-sushi.jp", p: "/best-sushi-shinjuku", ms: 176 },
  { d: "nyc-injury-law.com", p: "/personal-injury-manhattan", ms: 211 },
  { d: "florist-rome.it", p: "/wedding-flowers-trastevere", ms: 145 },
  { d: "movers-zurich.ch", p: "/long-distance-moving", ms: 163 },
];

const LiveEngineVisual = () => {
  const [tick, setTick] = useState(0);
  const [feed, setFeed] = useState<{ id: number; url: string; ms: number; status: "deploy" | "live" }[]>([]);
  const [throughput, setThroughput] = useState(1247);
  const [heatmap, setHeatmap] = useState<number[]>(() => Array.from({ length: 84 }, () => Math.random() * 0.4));
  const idRef = useRef(0);

  // Pipeline progress tick
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 60);
    return () => clearInterval(t);
  }, []);

  // Live publishing feed
  useEffect(() => {
    const push = () => {
      const u = ENGINE_URLS[idRef.current % ENGINE_URLS.length];
      idRef.current++;
      const id = idRef.current;
      setFeed((f) => [{ id, url: `${u.d}${u.p}`, ms: u.ms, status: "deploy" as const }, ...f].slice(0, 6));
      setTimeout(() => {
        setFeed((f) => f.map((row) => (row.id === id ? { ...row, status: "live" as const } : row)));
        setThroughput((n) => n + 1);
      }, 900);
    };
    push();
    const i = setInterval(push, 1700);
    return () => clearInterval(i);
  }, []);

  // Heatmap pulse
  useEffect(() => {
    const i = setInterval(() => {
      setHeatmap((h) => {
        const next = h.map((v) => Math.max(0.08, v * 0.94));
        const k = Math.floor(Math.random() * next.length);
        next[k] = 1;
        const k2 = Math.floor(Math.random() * next.length);
        next[k2] = Math.max(next[k2], 0.7);
        return next;
      });
    }, 380);
    return () => clearInterval(i);
  }, []);

  const nodes = ["SCAN", "BUILD", "DEPLOY", "INDEX"];
  const sparkPath = (() => {
    const pts: string[] = [];
    const n = 48;
    for (let i = 0; i < n; i++) {
      const x = (i / (n - 1)) * 100;
      const phase = (tick * 0.08 + i * 0.45);
      const y = 50 - (Math.sin(phase) * 12 + Math.sin(phase * 0.6) * 8 + 14);
      pts.push(`${i === 0 ? "M" : "L"}${x.toFixed(2)} ${y.toFixed(2)}`);
    }
    return pts.join(" ");
  })();
  const sparkFill = sparkPath + " L100 100 L0 100 Z";

  return (
    <div className="lo-engine">
      {/* Status header */}
      <div className="lo-engine-head">
        <div className="lo-engine-head-l">
          <span className="lo-engine-dot" />
          <span className="lo-engine-title">orbitrank.engine</span>
          <span className="lo-engine-meta">v2.1.0 · region: global</span>
        </div>
        <div className="lo-engine-head-r">
          <span className="lo-engine-kv"><span>throughput</span><b>{throughput.toLocaleString()}</b><span className="lo-engine-unit">/day</span></span>
          <span className="lo-engine-kv"><span>p95</span><b>187</b><span className="lo-engine-unit">ms</span></span>
        </div>
      </div>

      {/* Pipeline */}
      <div className="lo-engine-pipeline">
        {nodes.map((n, i) => (
          <div key={n} className="lo-engine-node-wrap">
            <div className="lo-engine-node">
              <span className="lo-engine-node-i">{String(i + 1).padStart(2, "0")}</span>
              <span className="lo-engine-node-l">{n}</span>
              <span className="lo-engine-node-pulse" style={{ animationDelay: `${i * 0.4}s` }} />
            </div>
            {i < nodes.length - 1 && (
              <div className="lo-engine-link">
                <div className="lo-engine-link-track" />
                <div className="lo-engine-link-flow" style={{ animationDelay: `${i * 0.35}s` }} />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Sparkline */}
      <div className="lo-engine-spark">
        <div className="lo-engine-spark-head">
          <span className="lo-engine-spark-l">PAGES INDEXED · LAST 60 MIN</span>
          <span className="lo-engine-spark-v">+{(tick % 240).toString().padStart(3, "0")}</span>
        </div>
        <svg viewBox="0 0 100 60" preserveAspectRatio="none" className="lo-engine-spark-svg">
          <defs>
            <linearGradient id="engSparkFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#00D97E" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#00D97E" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={sparkFill} fill="url(#engSparkFill)" />
          <path d={sparkPath} fill="none" stroke="#00D97E" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>

      {/* Live publishing feed */}
      <div className="lo-engine-stream">
        <div className="lo-engine-stream-head">
          <span>LIVE PUBLISHING STREAM</span>
          <span className="lo-engine-stream-count">{feed.filter(f => f.status === "live").length} live</span>
        </div>
        <div className="lo-engine-stream-list">
          {feed.map((row) => (
            <div key={row.id} className={`lo-engine-row is-${row.status}`}>
              <span className="lo-engine-row-status">
                {row.status === "live" ? "✓" : <span className="lo-engine-spin" />}
              </span>
              <span className="lo-engine-row-url">{row.url}</span>
              <span className="lo-engine-row-ms">{row.ms}ms</span>
            </div>
          ))}
        </div>
      </div>

      {/* Heatmap grid */}
      <div className="lo-engine-heat">
        <div className="lo-engine-heat-head">
          <span>INDEX ACTIVITY · 24H</span>
          <span className="lo-engine-heat-legend">
            <i style={{ background: "rgba(0,217,126,0.12)" }} />
            <i style={{ background: "rgba(0,217,126,0.35)" }} />
            <i style={{ background: "rgba(0,217,126,0.65)" }} />
            <i style={{ background: "#00D97E" }} />
          </span>
        </div>
        <div className="lo-engine-heat-grid">
          {heatmap.map((v, i) => (
            <span
              key={i}
              style={{
                background: `rgba(0,217,126,${0.08 + v * 0.72})`,
                boxShadow: v > 0.85 ? "0 0 8px rgba(0,217,126,0.8)" : "none",
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const TerminalSectionDesktop = () => {
  const [visibleCount, setVisibleCount] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (visibleCount >= FEED_STEPS.length) {
      const t = setTimeout(() => { setVisibleCount(0); setCycle(c => c + 1); }, 3200);
      return () => clearTimeout(t);
    }
    const delays = [350, 700, 900, 700, 700, 700, 1100];
    const t = setTimeout(() => setVisibleCount(c => c + 1), delays[visibleCount] || 700);
    return () => clearTimeout(t);
  }, [visibleCount]);

  return (
    <section className="lo-fresh-desktop hidden md:block">
      <div className="lo-fresh-inner">
        <div className="lo-fresh-grid">
          {/* LEFT */}
          <div className="lo-fresh-left">
            <span className="lo-fresh-chip"><span className="lo-fresh-chip-dot" />DAILY DEPLOYMENT</span>
            <h2 className="lo-fresh-title">
              Fresh pages.<br />
              <span className="lo-fresh-title-green">Every morning.</span>
            </h2>
            <p className="lo-fresh-sub">
              While you sleep, OrbitRank deploys new,<br />
              fully optimized pages to your website.<br />
              Day after day. Automatically.
            </p>

            {/* Live Feed card */}
            <div className="lo-feed-card">
              <div className="lo-feed-head">
                <span className="lo-feed-chip"><span className="lo-feed-chip-dot" />LIVE DEPLOYMENT FEED</span>
                <span className="lo-feed-live"><span className="lo-feed-live-dot" />LIVE</span>
              </div>

              <div className="lo-feed-list" key={cycle}>
                {FEED_STEPS.map((step, i) => {
                  const shown = i < visibleCount;
                  const isLast = i === FEED_STEPS.length - 1;
                  return (
                    <div key={i} className={`lo-feed-row ${shown ? "is-in" : ""} ${step.success ? "is-success" : ""}`}>
                      <span className="lo-feed-bullet" />
                      <span className="lo-feed-ico"><FeedIcon name={step.icon} /></span>
                      <span className="lo-feed-label">{step.label}</span>
                      <span className="lo-feed-time">{step.t}</span>
                    </div>
                  );
                })}
              </div>

              <div className="lo-feed-foot">
                <span className="lo-feed-foot-label"><Zap width={13} height={13} strokeWidth={2.2} style={{ color: "#FFD53A" }} /> DEPLOYING 24/7</span>
                <div className="lo-feed-bar">
                  <div className="lo-feed-bar-track" />
                  <div className="lo-feed-bar-flow" />
                </div>
              </div>
            </div>

            <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-start" }}>
              <a
                href="/blog"
                className="how-it-works-learn-cta"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  padding: "15px 24px",
                  borderRadius: 11,
                  background: "transparent",
                  border: `1px solid #A78BFA66`,
                  color: "#A78BFA",
                  fontWeight: 800,
                  fontSize: 14,
                  cursor: "pointer",
                  boxShadow: `0 0 0 1px #A78BFA22 inset, 0 6px 18px #A78BFA1f`,
                  textDecoration: "none",
                  transition: "transform .2s ease, box-shadow .2s ease, border-color .2s ease, color .2s ease",
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  textAlign: "center",
                }}
              >
                See how AI pages rank on Google →
              </a>
            </div>
          </div>

          {/* RIGHT — hero illustration */}
          <div className="lo-fresh-right">
            <LiveEngineVisual />


            {/* Feature row */}
            <div className="lo-fresh-features">
              {[
                { Ic: Zap,         t: "Automatic Publishing", d: "New optimized pages go live every single day." },
                { Ic: Target,      t: "Fully Optimized",      d: "SEO, schema, FAQs, images, internal links." },
                { Ic: null,        t: "Instant Indexing",     d: "Submitted to Google Search Console.", g: true },
                { Ic: ShieldCheck, t: "Hands-Off Growth",     d: "We handle the work. You get the results." },
              ].map((f, i) => (
                <div key={i} className="lo-fresh-feat">
                  <div className="lo-fresh-feat-ico">
                    {f.g ? <span style={{ fontFamily: "Inter, sans-serif", fontWeight: 900, fontSize: 22, color: "#00D97E", lineHeight: 1 }}>G</span> : f.Ic ? <f.Ic width={22} height={22} strokeWidth={2} /> : null}
                  </div>
                  <div className="lo-fresh-feat-t">{f.t}</div>
                  <div className="lo-fresh-feat-d">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom safety band */}
        <div className="lo-fresh-band">
          <div className="lo-fresh-band-ico"><ShieldCheck width={26} height={26} strokeWidth={2} /></div>
          <div>
            <div className="lo-fresh-band-t">You focus on your business. We handle the growth.</div>
            <div className="lo-fresh-band-d">OrbitRank runs in the background 24/7 to grow your visibility, traffic, and revenue.</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Stats ────────────────────────────────
const StatsSection = () => {
  const clients = useLiveCounter(5247, 3);
  const pages = useLiveCounter(1200000, 12);
  const keywords = useLiveCounter(84000, 5);
  const growing = useLiveCounter(5247, 2);
  return (
    <section className="lo-stats-section">
      <span className="lo-section-label">Live Metrics</span>
      <h2 className="lo-section-title">The engine never sleeps.</h2>
      <div className="lo-stats-grid">
        <div className="lo-stat-item"><div ref={clients.ref} className="lo-stat-val" style={{ transition: "all 1s ease" }}>{clients.value.toLocaleString()}</div><div className="lo-stat-lbl">Clients</div></div>
        <div className="lo-stat-item"><div ref={pages.ref} className="lo-stat-val" style={{ transition: "all 1s ease" }}>{pages.value.toLocaleString()}</div><div className="lo-stat-lbl">Pages Published</div></div>
        <div className="lo-stat-item"><div ref={keywords.ref} className="lo-stat-val" style={{ transition: "all 1s ease" }}>{keywords.value.toLocaleString()}</div><div className="lo-stat-lbl">Keywords Used</div></div>
        <div className="lo-stat-item"><div ref={growing.ref} className="lo-stat-val" style={{ transition: "all 1s ease" }}>{growing.value.toLocaleString()}</div><div className="lo-stat-lbl">Businesses Growing</div></div>
      </div>
    </section>
  );
};

// ─── Chart ────────────────────────────────
const ChartSection = () => {
  const chartRef = useRef<SVGSVGElement>(null);
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const svg = chartRef.current;
    if (!svg) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !drawn) {
        setDrawn(true);
      }
    }, { threshold: 0.3 });
    obs.observe(svg);
    return () => obs.disconnect();
  }, [drawn]);

  const linePath = "M0 100 Q50 90 100 75 T200 50 T300 25 T400 5";
  const fillPath = "M0 100 Q50 90 100 75 T200 50 T300 25 T400 5 V110 H0Z";

  return (
    <section className="lo-chart-section">
      <span className="lo-section-label">Always Running</span>
      <h2 className="lo-section-title">Your website never<br />stops growing.</h2>
      <p className="lo-section-sub">Every page we deploy is a new entry point. More pages. More keywords. More customers. It compounds daily.</p>
      <div className="lo-chart-box">
        <div className="lo-chart-label"><span className="lo-pulse" /> pages indexed — last 90 days</div>
        <svg ref={chartRef} className="lo-chart-svg" viewBox="0 0 400 110" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(0,217,126,0.3)" />
              <stop offset="100%" stopColor="rgba(0,217,126,0)" />
            </linearGradient>
          </defs>
          <path d={fillPath} fill="url(#chartFill)" className={drawn ? "lo-chart-fill-anim" : ""} style={{ opacity: drawn ? 1 : 0, transition: "opacity 0.5s 0.3s" }} />
          <path d={linePath} fill="none" stroke="#00D97E" strokeWidth="2"
            strokeDasharray="600" strokeDashoffset={drawn ? "0" : "600"}
            style={{ transition: "stroke-dashoffset 3s ease-in-out" }} />
          {drawn && (
            <circle r="4" fill="#00D97E" style={{ filter: "drop-shadow(0 0 6px rgba(0,217,126,0.8))" }}>
              <animateMotion dur="3s" fill="freeze" path={linePath} />
            </circle>
          )}
        </svg>
      </div>
    </section>
  );
};

// ─── Keywords ─────────────────────────────
const KeywordsSection = () => {
  const kws = [
    "best dentist berlin", "yoga studio near me", "car wash toronto", "accountant dublin",
    "restaurant delivery paris", "chiropractor los angeles", "vet clinic stockholm",
    "personal trainer miami", "moving company zurich", "physio clinic sydney",
    "hair salon london", "best lawyer nyc", "cleaning service amsterdam",
    "dog groomer melbourne", "gym near me madrid", "tax advisor vienna",
    "florist shop rome", "tutor near me chicago",
  ];
  return (
    <section className="lo-kw-section">
      <span className="lo-section-label">Keyword Intelligence</span>
      <h2 className="lo-section-title">We find what your<br />customers search for.</h2>
      <p className="lo-section-sub">The engine activates a pool of high-intent local keywords automatically. Every keyword is a new growth opportunity.</p>
      <div className="lo-kw-cloud">
        {kws.map((kw, i) => <span key={i} className="lo-kw-tag">{kw}</span>)}
      </div>
    </section>
  );
};

// ─── FAQ ──────────────────────────────────
const FAQ_ITEMS = [
  {
    q: "How does OrbitRank work with my website?",
    a: "You connect your website once — WordPress, Shopify, Wix, Webflow, Squarespace, or any other platform. We integrate with all of them. If you use something not on the list, we set up your integration personally. After connecting, OrbitRank runs fully automatically every single day.",
  },
  {
    q: "Do I need any technical knowledge?",
    a: "None at all. You enter your website URL, answer 5 quick questions about your business, and OrbitRank handles everything from that point. No coding, no agency, no effort.",
  },
  {
    q: "How long before I see results?",
    a: "OrbitRank doesn’t just publish content — it builds your entire online authority. New pages get indexed by Google within the first week. Rankings improve within 4 to 8 weeks. But beyond Google, OrbitRank also builds your directory presence, tracks competitors, monitors your AI search visibility, and optimizes your positioning across the web simultaneously. The results compound every single day.",
  },
  {
    q: "Does OrbitRank work for Google AND AI search like ChatGPT?",
    a: "Yes. OrbitRank is built for both. Every business on any plan gets Google SEO optimization automatically. Pro and Enterprise plans also include full AI Search visibility — we optimize your presence so ChatGPT, Gemini, and other AI engines recommend your business when people search for what you offer.",
  },
  {
    q: "What platforms do you support?",
    a: "WordPress, Shopify, Wix, Webflow, Squarespace — and any other platform. If your website is built on something else, we integrate it for you personally. No business gets left out.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. No contracts, no commitments, no questions asked. Cancel anytime directly from your account.",
  },
  {
    q: "What makes OrbitRank different from an SEO agency?",
    a: "An SEO agency charges $1,000 to $5,000 per month, requires your time, and moves slowly. OrbitRank runs 24/7 automatically — publishing content, building backlinks, tracking rankings, monitoring competitors, optimizing for AI search — all without you lifting a finger. More work. Better results. A fraction of the cost.",
  },
];

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  return (
    <section id="faq" style={{
      background: "var(--lo-bg)",
      padding: "72px 20px",
      borderTop: "1px solid var(--lo-border)",
    }}>
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <span className="lo-section-label" style={{ display: "block", textAlign: "center" }}>FAQ</span>
        <h2 className="lo-section-title" style={{ textAlign: "center" }}>Frequently Asked Questions</h2>
        <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQ_ITEMS.map((item, i) => {
            const open = openIdx === i;
            return (
              <div
                key={i}
                style={{
                  background: "var(--lo-section-strong)",
                  border: "1px solid var(--lo-border)",
                  borderRadius: 14,
                  overflow: "hidden",
                  transition: "border-color .2s ease",
                  borderColor: open ? "rgba(34,197,94,0.5)" : "var(--lo-border)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? null : i)}
                  aria-expanded={open}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "18px 20px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    color: "var(--lo-text)",
                    fontSize: 15,
                    fontWeight: 600,
                    lineHeight: 1.4,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  <span>{item.q}</span>
                  <span
                    aria-hidden
                    style={{
                      flexShrink: 0,
                      width: 28,
                      height: 28,
                      borderRadius: 999,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "rgba(34,197,94,0.12)",
                      color: "#22c55e",
                      fontSize: 18,
                      fontWeight: 700,
                      lineHeight: 1,
                      transition: "transform .25s ease",
                      transform: open ? "rotate(45deg)" : "rotate(0deg)",
                    }}
                  >
                    +
                  </span>
                </button>
                <div
                  style={{
                    maxHeight: open ? 600 : 0,
                    transition: "max-height .35s ease",
                    overflow: "hidden",
                  }}
                >
                  <div style={{
                    padding: "0 20px 20px",
                    color: "var(--lo-text-soft)",
                    fontSize: 14,
                    lineHeight: 1.65,
                  }}>
                    {item.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ─── Pricing ──────────────────────────────
const Pricing = () => {
  const navigate = useNavigate();
  const { openCheckout } = usePaddleCheckout();
  const [loadingTier, setLoadingTier] = useState<string | null>(null);

  const handleCheckout = async (priceId: string) => {
    setLoadingTier(priceId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate(`/auth?redirect=/pricing`);
        return;
      }
      await openCheckout({
        priceId,
        customerEmail: session.user.email,
        customData: { userId: session.user.id },
        successUrl: `${window.location.origin}/dashboard?checkout=success`,
      });
    } finally {
      setLoadingTier(null);
    }
  };

  const trialNote = (
    <p style={{ fontSize: 11, color: "var(--lo-text-muted)", textAlign: "center", marginTop: 8 }}>
      7-day free trial · Cancel anytime · No commitment
    </p>
  );

  return (
    <section id="pricing" className="lo-pricing">
      <span className="lo-section-label">Simple, Transparent Pricing</span>
      <h2 className="lo-section-title">One price. <span className="lo-pricing-accent">Everything</span> included.</h2>
      <p className="lo-section-sub">No hidden fees. No contracts. Cancel anytime.<br className="lo-pricing-br" />Everything a top SEO agency does — fully automated.</p>

      <div className="lo-pricing-trust-top">
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">⚡</span><div><div className="lo-pricing-trust-title">Fully Automated</div><div className="lo-pricing-trust-sub">AI runs everything</div></div></div>
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">🛡</span><div><div className="lo-pricing-trust-title">No Contracts</div><div className="lo-pricing-trust-sub">Cancel anytime</div></div></div>
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">✓</span><div><div className="lo-pricing-trust-title">Everything Included</div><div className="lo-pricing-trust-sub">All features, zero limits</div></div></div>
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">🚀</span><div><div className="lo-pricing-trust-title">Deploys Daily</div><div className="lo-pricing-trust-sub">New pages every morning</div></div></div>
      </div>

      <div className="lo-pricing-cards">
        <div className="lo-price-card">
          <div className="lo-price-name">Starter</div>
          <div className="lo-price-amount">$59</div>
          <div className="lo-price-period">/month · cancel anytime</div>
          <ul className="lo-price-features">
            <li>1 location · 30 pages/month</li><li>Rank tracking</li>
            <li>20 directory backlinks</li><li>Google Search Console</li>
          </ul>
          <button className="lo-price-btn outline" disabled={loadingTier !== null} onClick={() => handleCheckout("starter_monthly")}>
            {loadingTier === "starter_monthly" ? "Loading..." : "Get Started →"}
          </button>
          {trialNote}
        </div>
        <div className="lo-price-card featured">
          <div className="lo-price-badge">⭐ Most Popular</div>
          <div className="lo-price-name">Pro</div>
          <div className="lo-price-amount">$99</div>
          <div className="lo-price-period">/month · cancel anytime</div>
          <ul className="lo-price-features">
            <li>3 locations · 90 pages/month</li><li>Rank tracking + competitor analysis</li>
            <li>Page speed monitoring</li><li>20 directory backlinks</li>
            <li>Monthly AI report</li><li>Google Search Console</li>
          </ul>
          <button className="lo-price-btn primary" disabled={loadingTier !== null} onClick={() => handleCheckout("pro_monthly")}>
            {loadingTier === "pro_monthly" ? "Loading..." : "Launch Now 🚀"}
          </button>
          {trialNote}
        </div>
        <div className="lo-price-card">
          <div className="lo-price-name">Enterprise</div>
          <div className="lo-price-amount">$199</div>
          <div className="lo-price-period">/month · cancel anytime</div>
          <ul className="lo-price-features">
            <li>Unlimited locations</li><li>Unlimited pages/month</li>
            <li>All features included</li><li>Priority support</li><li>White label ready</li>
          </ul>
          <button className="lo-price-btn dark" disabled={loadingTier !== null} onClick={() => handleCheckout("enterprise_monthly")}>
            {loadingTier === "enterprise_monthly" ? "Loading..." : "Scale Up →"}
          </button>
          {trialNote}
        </div>
      </div>

      <div className="lo-pricing-trust-bottom">
        <div className="lo-pricing-trust-headline">Trusted by businesses<br />that want real results.</div>
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">⏱</span><div><div className="lo-pricing-trust-title">Automated daily</div><div className="lo-pricing-trust-sub">deployment</div></div></div>
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">🔍</span><div><div className="lo-pricing-trust-title">Google indexed</div><div className="lo-pricing-trust-sub">in hours</div></div></div>
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">📈</span><div><div className="lo-pricing-trust-title">Real results</div><div className="lo-pricing-trust-sub">real growth</div></div></div>
        <div className="lo-pricing-trust-item"><span className="lo-pricing-trust-icon">✓</span><div><div className="lo-pricing-trust-title">24/7 system</div><div className="lo-pricing-trust-sub">always working</div></div></div>
      </div>

      <div className="lo-pricing-foot">🛡 7-day free trial · Cancel anytime · No commitment</div>
    </section>
  );
};

// ─── Final CTA ────────────────────────────
const FinalCTA = () => {
  const navigate = useNavigate();
  return (
    <section className="lo-cta-final">
      <div className="lo-cta-box">
        <h2 className="lo-cta-title">Your system is ready<br />to launch.</h2>
        <p className="lo-cta-sub">Join businesses that run on autopilot every day.<br />No agency. No effort. Just growth — automatically.</p>
        <button className="lo-btn-primary" style={{ maxWidth: 340, margin: "0 auto" }} onClick={() => navigate("/activate")}>🚀 Activate Your Orbit →</button>
        <p className="lo-cta-note">✦ No setup  ·  No agency  ·  Fully autonomous  ·  Trusted by 5,000+ businesses</p>
      </div>
    </section>
  );
};

// ─── Footer ───────────────────────────────
const Footer = () => (
  <footer className="lo-footer">
    <div className="lo-footer-top">
      <div className="lo-footer-brand">
        <div className="lo-footer-logo" style={{ gap: 2 }}><img src={logoMark} alt="OrbitRank logo" style={{ width: 36, height: 36, objectFit: "contain", background: "transparent" }} /><span><span style={{ color: "var(--lo-text)" }}>Orbit</span><span style={{ color: "hsl(var(--primary))" }}>Rank</span></span></div>
        <div className="lo-footer-tagline">The autonomous SEO engine that ranks your business — automatically, every single day.</div>
      </div>
      <div>
        <div className="lo-footer-col-title">Product</div>
        <div className="lo-footer-links"><a href="#how-it-works">How It Works</a><a href="#">Features</a><a href="#">Pricing</a><a href="/dashboard">Dashboard</a></div>
      </div>
      <div>
        <div className="lo-footer-col-title">Features</div>
        <div className="lo-footer-links"><a href="#">Daily SEO Pages</a><a href="#">Rank Tracking</a><a href="#">Competitor Analysis</a><a href="#">Backlink Building</a></div>
      </div>
      <div>
        <div className="lo-footer-col-title">Company</div>
        <div className="lo-footer-links"><a href="#">About</a><a href="#">Contact</a><a href="#">Support</a><a href="/auth">Login</a></div>
      </div>
      <div>
        <div className="lo-footer-col-title">Legal</div>
        <div className="lo-footer-links"><a href="/privacy">Privacy Policy</a><a href="/terms">Terms of Service</a><a href="/refund-policy">Refund Policy</a></div>
      </div>
    </div>
    <div className="lo-footer-bottom">
      <div className="lo-footer-copy">OrbitRank © 2026  ·  v2.1.0  ·  All systems running</div>
      <div className="lo-footer-legal"><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/refund-policy">Refunds</a></div>
    </div>
  </footer>
);

// ─── Page ─────────────────────────────────
const Index = () => {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      // Wait for sections to render
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempt < 10) {
          setTimeout(() => tryScroll(attempt + 1), 150);
        }
      };
      tryScroll();
    }
  }, []);
  return (
  <div style={{ background: "var(--lo-bg)", color: "var(--lo-text)", fontFamily: "'Plus Jakarta Sans', sans-serif", overflowX: "hidden" }}>
    <div style={{
      position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
      background: "var(--lo-page-glow)",
    }} />
    <div className="md:hidden">
      <Nav />
    </div>
    
    <div style={{ position: "relative", zIndex: 1 }}>
      <DesktopHero />
      <div className="md:hidden">
        <Hero />
        <TrustBridge />
        <FoundEverywhere />
      </div>
      <GrowthEngineSection />

      <TweetTestimonials />
      <AiVisibilitySection />
      <FlowSection />
      
      <HowItWorks />
      <section style={{ background: "var(--lo-section-strong)", padding: "64px 24px", textAlign: "center", borderTop: "1px solid var(--lo-border)", borderBottom: "1px solid var(--lo-border)" }}>
        <h2 style={{ fontSize: "clamp(24px, 5.5vw, 38px)", fontWeight: 900, color: "var(--lo-text)", margin: "0 auto 24px", maxWidth: 680, lineHeight: 1.2, letterSpacing: "-0.5px" }}>
          Ready? Let's build your engine.
        </h2>
        <a href="/activate" className="lo-btn-primary" style={{ display: "inline-block", width: "auto", textDecoration: "none" }}>
          🚀 Launch My SEO →
        </a>
      </section>
      <Features />
      <TerminalSection />
      <div className="md:hidden"><StatsSection /></div>
      <div className="md:hidden"><ChartSection /></div>
      <div className="md:hidden"><KeywordsSection /></div>
      <FAQSection />
      <Pricing />
      <div className="md:hidden"><FinalCTA /></div>
      <Footer />
      <ChatWidget />
    </div>
  </div>
  );
};

export default Index;
